import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { computePayout, arrangementForArtist } from '@/domain/commissionEngine';
import { buildQueue, partitionQueue } from '@/domain/queueEngine';
import {
  cashTimingStrip,
  merchBreakdown,
  depositsReceivedBreakdown,
  weekDays as reconWeekDays,
  weekdayOf,
  type TimingStripDay,
  type MerchBreakdownRow,
  type DepositReceivedRow,
} from '@/domain/reconciliationFlow';
import { regionLabelFor } from '@/data/melbourneAreas';
import { buildTopupTrack, TOPUP_FINAL_REMINDER_DAYS_BEFORE, type TopupTrackState } from '@/domain/topupReminders';
import { type ArtistWeeklyTarget } from '@/data/reportingTargets';
import { reviewsInRange } from '@/data/reviewsFixture';
import { aud, customerName } from '@/utils/format';
import {
  bookingsByDepositId,
  depositMoneyInAud,
  depositStanding,
  heldMoneyAud,
  isCommittedBooking,
  isDepositOnFile,
  onFileValidUntil,
  studioDayKey,
} from '@atink/domain';
import { calendarWeekDays } from '@/domain/calendarWeek';
import { dayKey } from '@/utils/studioDay';
import { salesByBookingId, sessionTakingsFor } from '@/domain/sessionTakings';
import type {
  Artist,
  Booking,
  BookingSource,
  Channel,
  CustomerRecord,
  DayOfWeek,
  Deposit,
  MarketingCampaign,
  Piece,
  RescheduleReason,
  SalePayment,
  StudioId,
} from '@/domain/types';

/**
 * Reporting selectors (Prompt F). One read-only selector per metric, computed
 * live from the brain store for a given sim day or week. Nothing here mutates
 * and nothing recomputes commission its own way - the revenue split reads E2's
 * `commissionEngine`. Every number the reporting screen shows comes from here,
 * so the screen holds no hardcoded metric constants.
 *
 * Store is read via `getState()` (pure, testable). Components stay reactive by
 * subscribing to the slices they show and calling these in render/useMemo.
 * "Now" is always the sim clock, never `Date.now()`.
 */

// --- Time helpers ----------------------------------------------------------

/** The sim "now" as an ISO string. */
export function reportingNow(): string {
  return useSimulationStore.getState().clockIso;
}

/** Studio calendar-day key ('YYYY-MM-DD'): the Melbourne day of the instant, whatever
 * offset the ISO carries (a Z stamp at 23:30Z is the next Melbourne day). */
export function dayKeyOf(iso: string): string {
  return dayKey(iso);
}

/**
 * `count` calendar-day keys ending on (and including) `endDayKey`, oldest first.
 * Anchored at noon UTC and stepped by whole days so the date slice never drifts.
 */
export function windowDays(endDayKey: string, count: number): string[] {
  const end = new Date(`${endDayKey}T12:00:00Z`);
  const days: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(end.getTime() - i * 24 * 60 * 60_000);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

/** The default reporting window: the last 7 sim days, ending today. */
export function reportingWeekDays(now: string = reportingNow()): string[] {
  return windowDays(dayKeyOf(now), 7);
}

/**
 * Every calendar-day key from `startDayKey` to `endDayKey` inclusive, oldest
 * first. Order-tolerant (swaps if start is after end). Anchored at noon UTC so
 * the date slice never drifts. Powers the reporting date-range filter.
 */
export function daysInRange(startDayKey: string, endDayKey: string): string[] {
  const a = new Date(`${startDayKey}T12:00:00Z`).getTime();
  const b = new Date(`${endDayKey}T12:00:00Z`).getTime();
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const out: string[] = [];
  for (let t = lo; t <= hi; t += 24 * 60 * 60_000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}

// --- Shared types ----------------------------------------------------------

/** One point in a per-day time series. */
export interface DayCount {
  dayIso: string;
  count: number;
}

/** A per-day money series (count + dollars). */
export interface DayMoney {
  dayIso: string;
  count: number;
  amountAud: number;
}

// --- Store access + filters ------------------------------------------------

function studioOf(customerId: string, customers: Record<string, CustomerRecord>): StudioId | undefined {
  return customers[customerId]?.primaryStudioId;
}

function inStudio(studioId: StudioId | null | undefined, target: StudioId | undefined): boolean {
  return !studioId || studioId === target;
}

/**
 * The day a session's money counts on: the studio day it STARTED, as payouts and
 * the H rule count it (7kv6, decided with Anas 30 Sep). A session running past
 * 6pm is paid before the 6pm clock-off — on a double day both sessions are
 * checked out by 6pm — so a late checkout timestamp (a session that started Sun
 * 23:00 and was checked out Mon 03:00) never moves it to the next day.
 */
function completedDayKey(b: Booking): string {
  return dayKeyOf(b.start);
}

// --- Bookings completed ----------------------------------------------------

export function completedBookingsByDay(days: readonly string[], studioId?: StudioId | null): DayCount[] {
  const { bookings } = useBrainStore.getState();
  const set = new Set(days);
  const perDay = new Map<string, number>(days.map((d) => [d, 0]));
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed') continue;
    if (!inStudio(studioId, b.studioId)) continue;
    const key = completedDayKey(b);
    if (!set.has(key)) continue;
    perDay.set(key, (perDay.get(key) ?? 0) + 1);
  }
  return days.map((d) => ({ dayIso: d, count: perDay.get(d) ?? 0 }));
}

/** Weekly running tally of completed sessions across the window. */
export function weeklyCompletedTally(days: readonly string[], studioId?: StudioId | null): number {
  return completedBookingsByDay(days, studioId).reduce((sum, d) => sum + d.count, 0);
}

// --- Deposits paid ---------------------------------------------------------

/**
 * Deposits paid per day by COUNT (funnels, conversion). Its `amountAud` is the
 * old status/paidAt rule; money surfaces read `depositsReceivedIn` /
 * `depositsReceivedByDay` (the ledger's money in, the Day sheet's rule; B10 9mb8).
 */
export function depositsPaidByDay(days: readonly string[], studioId?: StudioId | null): DayMoney[] {
  const { deposits } = useBrainStore.getState();
  const set = new Set(days);
  const perDay = new Map<string, { count: number; amountAud: number }>(
    days.map((d) => [d, { count: 0, amountAud: 0 }]),
  );
  for (const dep of Object.values(deposits)) {
    if (dep.status !== 'paid' || !dep.paidAt) continue;
    if (!inStudio(studioId, dep.studioId)) continue;
    const key = dayKeyOf(dep.paidAt);
    if (!set.has(key)) continue;
    const cell = perDay.get(key)!;
    cell.count += 1;
    cell.amountAud += dep.amountPaid ?? dep.amountAud;
  }
  return days.map((d) => ({ dayIso: d, ...perDay.get(d)! }));
}

// --- New leads -------------------------------------------------------------

/** Brand-new leads per day - a customer's first touch that day. Replies never move `firstTouchAt`, so they are excluded by construction. */
export function newLeadsByDay(days: readonly string[], studioId?: StudioId | null): DayCount[] {
  const { customers } = useBrainStore.getState();
  const set = new Set(days);
  const perDay = new Map<string, number>(days.map((d) => [d, 0]));
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    const key = dayKeyOf(c.source.firstTouchAt);
    if (!set.has(key)) continue;
    perDay.set(key, (perDay.get(key) ?? 0) + 1);
  }
  return days.map((d) => ({ dayIso: d, count: perDay.get(d) ?? 0 }));
}

/** New leads in the window split by acquisition channel. */
export function newLeadsByChannel(
  days: readonly string[],
  studioId?: StudioId | null,
): Partial<Record<Channel, number>> {
  const { customers } = useBrainStore.getState();
  const set = new Set(days);
  const out: Partial<Record<Channel, number>> = {};
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    if (!set.has(dayKeyOf(c.source.firstTouchAt))) continue;
    out[c.source.channel] = (out[c.source.channel] ?? 0) + 1;
  }
  return out;
}

// --- Calls per agent -------------------------------------------------------

export interface AgentCalls {
  agentId: string;
  outbound: number;
  inbound: number;
}

/**
 * Outbound + inbound calls per agent in the window. Read from the durable voice
 * conversation entries stamped with an `agentId` at the call site (callFlow).
 * Calls are never seeded - they accrue live as the demo dials - so this returns
 * zeros until a call is made, by design (no invented numbers).
 */
export function callsPerAgent(days: readonly string[], studioId?: StudioId | null): AgentCalls[] {
  const { customers } = useBrainStore.getState();
  const set = new Set(days);
  const byAgent = new Map<string, AgentCalls>();
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    for (const entry of c.conversation) {
      if (entry.channel !== 'voice' || !entry.agentId) continue;
      if (!set.has(dayKeyOf(entry.timestamp))) continue;
      const row = byAgent.get(entry.agentId) ?? { agentId: entry.agentId, outbound: 0, inbound: 0 };
      if (entry.direction === 'out') row.outbound += 1;
      else row.inbound += 1;
      byAgent.set(entry.agentId, row);
    }
  }
  return [...byAgent.values()];
}

// --- Deposits per agent ----------------------------------------------------

/** Paid deposits in the window credited to each agent (from `takenByAgentId`). */
export function depositsPerAgent(days: readonly string[], studioId?: StudioId | null): Record<string, number> {
  const { deposits } = useBrainStore.getState();
  const set = new Set(days);
  const out: Record<string, number> = {};
  for (const dep of Object.values(deposits)) {
    if (dep.status !== 'paid' || !dep.paidAt || !dep.takenByAgentId) continue;
    if (!inStudio(studioId, dep.studioId)) continue;
    if (!set.has(dayKeyOf(dep.paidAt))) continue;
    out[dep.takenByAgentId] = (out[dep.takenByAgentId] ?? 0) + 1;
  }
  return out;
}

// --- Per-agent performance table -------------------------------------------

export interface AgentPerformanceRow {
  agentId: string;
  name: string;
  outboundCalls: number;
  inboundCalls: number;
  deposits: number;
  /** deposits / total calls; null when no calls were made yet. */
  conversion: number | null;
}

/**
 * The live replacement for the hardcoded per-agent table. One row per
 * bookings-agent, merging calls (live) + deposits (attributed). Conversion is
 * null until calls exist rather than dividing by zero.
 */
export function agentPerformance(days: readonly string[], studioId?: StudioId | null): AgentPerformanceRow[] {
  const { agents } = useBrainStore.getState();
  const calls = new Map(callsPerAgent(days, studioId).map((c) => [c.agentId, c]));
  const deposits = depositsPerAgent(days, studioId);
  return Object.values(agents)
    .filter((a) => a.role === 'bookings-agent')
    .filter((a) => !studioId || a.assignedStudios.includes(studioId))
    .map((a) => {
      const c = calls.get(a.id);
      const outboundCalls = c?.outbound ?? 0;
      const inboundCalls = c?.inbound ?? 0;
      const totalCalls = outboundCalls + inboundCalls;
      const dep = deposits[a.id] ?? 0;
      return {
        agentId: a.id,
        name: `${a.firstName} ${a.lastName}`,
        outboundCalls,
        inboundCalls,
        deposits: dep,
        conversion: totalCalls > 0 ? dep / totalCalls : null,
      };
    })
    .sort((x, y) => y.deposits - x.deposits);
}

// --- Rebooks ---------------------------------------------------------------

/**
 * Shared rebook predicate: a session that was rebooked in the chair. Exported so
 * the Leaderboard / LeaderboardTV read the same definition the reporting screen
 * uses, rather than an independent inline check (Prompt F, Stage 5 alignment).
 */
export function rebookedInChair(b: Booking): boolean {
  return b.rebookOutcome?.kind === 'rebookedInChair';
}

/** A rebook = a completed session rebooked in the chair (mirrors RebookAttributionPanel). */
function isRebook(b: Booking): boolean {
  return b.status === 'completed' && rebookedInChair(b);
}

function rebookDayKey(b: Booking): string {
  return dayKeyOf(b.rebookAttribution?.capturedAt ?? b.checkedOutAt ?? b.paidAt ?? b.start);
}

/** Rebooks per artist in the window, keyed by the artist whose chair it was. */
export function rebooksByArtist(days: readonly string[], studioId?: StudioId | null): Record<string, number> {
  const { bookings } = useBrainStore.getState();
  const set = new Set(days);
  const out: Record<string, number> = {};
  for (const b of Object.values(bookings)) {
    if (!isRebook(b)) continue;
    if (!inStudio(studioId, b.studioId)) continue;
    if (!set.has(rebookDayKey(b))) continue;
    out[b.artistId] = (out[b.artistId] ?? 0) + 1;
  }
  return out;
}

/** Rebooks per store in the window. */
export function rebooksByStore(days: readonly string[]): Record<string, number> {
  const { bookings } = useBrainStore.getState();
  const set = new Set(days);
  const out: Record<string, number> = {};
  for (const b of Object.values(bookings)) {
    if (!isRebook(b)) continue;
    if (!set.has(rebookDayKey(b))) continue;
    out[b.studioId] = (out[b.studioId] ?? 0) + 1;
  }
  return out;
}

export interface RebookRate {
  rebooks: number;
  completed: number;
  /** rebooks / completed sessions in the window; 0 when nothing completed. */
  rate: number;
}

/** The 50 percent rebook baseline the studio measures against. */
export const REBOOK_BASELINE = 0.5;
/** The 40 percent manager target from the attribution spec (Rebooks tab reference line). */
export const REBOOK_MANAGER_TARGET = 0.4;

/**
 * "No future booking" at the END of the work — a one-off (no piece), or a piece
 * that is complete — is not a decline and can't be rebooked (B8 / 76vb, Anas
 * 23 Sep + 1 Oct Q4): it counts in neither the declines, the leak list nor the
 * rebook-rate denominator. Only a no-rebook MID-PIECE is the leak.
 */
export function isEndOfWorkNoRebook(b: Booking, pieces: Record<string, Piece>): boolean {
  if (b.rebookOutcome?.kind !== 'noRebookDepositApplied') return false;
  return !b.pieceId || pieces[b.pieceId]?.status === 'completed';
}

export function rebookRate(days: readonly string[], studioId?: StudioId | null): RebookRate {
  const { bookings, pieces } = useBrainStore.getState();
  const set = new Set(days);
  let rebooks = 0;
  let completed = 0;
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed') continue;
    if (!inStudio(studioId, b.studioId)) continue;
    if (!set.has(completedDayKey(b))) continue;
    if (isEndOfWorkNoRebook(b, pieces)) continue;
    completed += 1;
    if (rebookedInChair(b)) rebooks += 1;
  }
  return { rebooks, completed, rate: completed > 0 ? rebooks / completed : 0 };
}

// --- Rebook attribution (page-wide, studio + date scoped) ------------------

export interface RebookOutcomeCounts {
  rebooked: number;
  declined: number;
  deferred: number;
  deferredBooked: number;
  deferredDeclined: number;
  deferredNoContact: number;
}

export interface RebookStaffRow {
  id: string;
  name: string;
  role: string;
  rebooks: number;
}

export interface RebookAttributionSummary {
  overall: RebookOutcomeCounts;
  byStudio: Record<string, RebookOutcomeCounts>;
  staffRows: RebookStaffRow[];
}

const ZERO_REBOOK_COUNTS: RebookOutcomeCounts = {
  rebooked: 0,
  declined: 0,
  deferred: 0,
  deferredBooked: 0,
  deferredDeclined: 0,
  deferredNoContact: 0,
};

function tallyRebookOutcome(b: Booking, into: RebookOutcomeCounts, pieces: Record<string, Piece>): void {
  const o = b.rebookOutcome;
  if (!o) return;
  if (o.kind === 'rebookedInChair') into.rebooked += 1;
  else if (o.kind === 'noRebookDepositApplied') {
    if (!isEndOfWorkNoRebook(b, pieces)) into.declined += 1;
  }
  else if (o.kind === 'rebookDeferredToSales') {
    into.deferred += 1;
    if (o.resolvedOutcome === 'booked') into.deferredBooked += 1;
    else if (o.resolvedOutcome === 'declined') into.deferredDeclined += 1;
    else if (o.resolvedOutcome === 'noContact') into.deferredNoContact += 1;
  }
}

const FACILITATOR_ROLE_LABEL: Record<string, string> = {
  artist: 'Artist',
  floorManager: 'Floor manager',
  salesTeam: 'Sales team',
  otherStaff: 'Other staff',
};

/**
 * Rebook attribution scoped to the shell's studio + date range: overall + per-studio
 * outcome counts, plus the staff (artists, managers, agents) who facilitated in-chair
 * rebooks. Completed sessions are placed by their checkout day.
 */
export function rebookAttribution(
  days: readonly string[],
  studioId?: StudioId | null,
): RebookAttributionSummary {
  const { bookings, artists, agents, pieces } = useBrainStore.getState();
  const set = new Set(days);
  const overall: RebookOutcomeCounts = { ...ZERO_REBOOK_COUNTS };
  const byStudio: Record<string, RebookOutcomeCounts> = {};
  const byStaff: Record<string, RebookStaffRow> = {};

  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed' || !b.rebookOutcome) continue;
    if (!inStudio(studioId, b.studioId)) continue;
    if (!set.has(completedDayKey(b))) continue;
    tallyRebookOutcome(b, overall, pieces);
    tallyRebookOutcome(b, (byStudio[b.studioId] ??= { ...ZERO_REBOOK_COUNTS }), pieces);

    if (b.rebookOutcome.kind === 'rebookedInChair' && b.rebookAttribution) {
      const sid = b.rebookAttribution.facilitatedByStaffId;
      if (!byStaff[sid]) {
        const artist = artists[sid];
        const agent = agents[sid];
        byStaff[sid] = {
          id: sid,
          name: artist?.displayName || artist?.firstName || agent?.firstName || sid,
          role: FACILITATOR_ROLE_LABEL[b.rebookAttribution.facilitatedBy] ?? 'Other staff',
          rebooks: 0,
        };
      }
      byStaff[sid].rebooks += 1;
    }
  }

  const staffRows = Object.values(byStaff).sort((a, b) => b.rebooks - a.rebooks);
  return { overall, byStudio, staffRows };
}

// --- Revenue: ours vs artist ----------------------------------------------

export interface StoreDayRevenue {
  studioId: StudioId;
  dayIso: string;
  /** The house share ("ours") across the day's completed sessions. */
  oursAud: number;
  /** The artists' envelopes across the day's completed sessions. */
  artistAud: number;
  totalAud: number;
}

/**
 * Revenue per store per day, split ours-vs-artist via E2's commission engine.
 * Gross for a completed session is its sale balance + deposit applied (merch
 * excluded, since the studio keeps merch whole); the engine then splits it.
 */
export function revenueByStoreDay(days: readonly string[], studioId?: StudioId | null): StoreDayRevenue[] {
  const cells = new Map<string, StoreDayRevenue>();
  for (const g of artistDaySplits(days, (b) => inStudio(studioId, b.studioId))) {
    const k = `${g.studioId}:${g.dayIso}`;
    const cell = cells.get(k) ?? { studioId: g.studioId, dayIso: g.dayIso, oursAud: 0, artistAud: 0, totalAud: 0 };
    cell.oursAud += g.oursAud;
    cell.artistAud += g.artistAud;
    cell.totalAud += g.grossAud;
    cells.set(k, cell);
  }
  return [...cells.values()].sort((a, b) =>
    a.dayIso === b.dayIso ? a.studioId.localeCompare(b.studioId) : a.dayIso.localeCompare(b.dayIso),
  );
}

interface ArtistDaySplit {
  studioId: StudioId;
  artistId: string;
  dayIso: string;
  sessions: number;
  grossAud: number;
  oursAud: number;
  artistAud: number;
}

/**
 * Completed sessions in the window, grouped per studio · artist · day (the unit
 * an envelope is paid in), each split at the commission IN FORCE THAT DAY: the
 * arrangement snapshotted on the day's payout record where one exists, else the
 * artist's current one (B10; commission became editable in B9 / 75nk). Editing a
 * rate therefore never rewrites a paid day's figures, and a flat day fee is
 * counted once per day worked, not once per session.
 */
function artistDaySplits(days: readonly string[], include: (b: Booking) => boolean): ArtistDaySplit[] {
  const { bookings, salePayments, artists, payoutRecords } = useBrainStore.getState();
  const salesByBooking = salesByBookingId(salePayments);
  const set = new Set(days);
  const groups = new Map<string, { studioId: StudioId; artistId: string; dayIso: string; lines: Array<{ bookingId: string; amountAud: number }> }>();
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed' || !include(b)) continue;
    const day = completedDayKey(b);
    if (!set.has(day)) continue;
    const grossAud = sessionGrossAud(b, salesByBooking, artists);
    if (grossAud <= 0) continue;
    const k = `${b.studioId}|${b.artistId}|${day}`;
    const g = groups.get(k) ?? { studioId: b.studioId, artistId: b.artistId, dayIso: day, lines: [] };
    g.lines.push({ bookingId: b.id, amountAud: grossAud });
    groups.set(k, g);
  }
  return [...groups.values()].map((g) => {
    const artist = artists[g.artistId];
    const arrangement =
      payoutRecords[`payout-${g.studioId}-${g.artistId}-${g.dayIso}`]?.arrangement ??
      (artist ? arrangementForArtist(artist) : ({ kind: 'percentage', artistShare: 0.5 } as const));
    const split = computePayout(arrangement, g.lines);
    return {
      studioId: g.studioId,
      artistId: g.artistId,
      dayIso: g.dayIso,
      sessions: g.lines.length,
      grossAud: split.grossAud,
      oursAud: split.houseShareAud,
      artistAud: split.payoutAud,
    };
  });
}

// --- Customer geography ----------------------------------------------------

export interface RegionCount {
  region: string;
  count: number;
}

/**
 * Customer geography by Melbourne area (VIC) or city (non-VIC), largest first.
 * `state` filters to customers from that state (e.g. 'VIC' for the Melbourne
 * compass-area breakdown); omit for all states.
 */
export function customerGeography(studioId?: StudioId | null, state?: string | null): RegionCount[] {
  const { customers } = useBrainStore.getState();
  const byRegion = new Map<string, number>();
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    if (state && c.state !== state) continue;
    const region = regionLabelFor(c.suburb, c.state);
    byRegion.set(region, (byRegion.get(region) ?? 0) + 1);
  }
  return [...byRegion.entries()]
    .map(([region, count]) => ({ region, count }))
    .sort((a, b) => b.count - a.count || a.region.localeCompare(b.region));
}

// --- Queue cohort sizes ----------------------------------------------------

export interface CohortSizes {
  /** Deposit taken but no date locked in (money on the books). */
  moneyOnBooks: number;
  /** Unfinished pieces + open rebook follow-ups. */
  openBusiness: number;
  /** Reds we never reached. */
  lightReds: number;
  /** Reds who said no. */
  darkReds: number;
}

const UNFINISHED_PIECE_STATUSES = new Set(['in-progress', 'paused-will-return']);

export function cohortSizes(studioId?: StudioId | null): CohortSizes {
  const { deposits, sequenceState, customers, pieces, rebookFollowupTasks } = useBrainStore.getState();

  let moneyOnBooks = 0;
  for (const dep of Object.values(deposits)) {
    if (!inStudio(studioId, dep.studioId)) continue;
    const money = dep.status === 'pending' || (dep.status === 'paid' && !dep.bookingId);
    if (money) moneyOnBooks += 1;
  }

  let lightReds = 0;
  let darkReds = 0;
  for (const [customerId, seq] of Object.entries(sequenceState)) {
    if (seq.stage !== 'red') continue;
    if (!inStudio(studioId, studioOf(customerId, customers))) continue;
    if (seq.redKind === 'never-reached') lightReds += 1;
    else if (seq.redKind === 'said-no') darkReds += 1;
  }

  let openBusiness = 0;
  for (const p of Object.values(pieces)) {
    if (!UNFINISHED_PIECE_STATUSES.has(p.status)) continue;
    if (!inStudio(studioId, studioOf(p.customerId, customers))) continue;
    openBusiness += 1;
  }
  for (const t of Object.values(rebookFollowupTasks)) {
    if (t.resolvedAt) continue;
    if (!inStudio(studioId, t.studioId)) continue;
    openBusiness += 1;
  }

  return { moneyOnBooks, openBusiness, lightReds, darkReds };
}

// --- Funnel ----------------------------------------------------------------

export interface FunnelStep {
  stage: string;
  value: number;
  /** How to render the value: a dollar spend or a plain count. */
  kind: 'spend' | 'count';
}

function qualified(c: CustomerRecord): boolean {
  // ASSUMPTION: "qualified" = past the initial 'new' stage (agent has engaged),
  // pending the client's own qualification definition.
  return c.leadStage !== 'new' && c.leadStage !== 'do-not-contact';
}

export function metaAdSpend(): number {
  const { marketingCampaigns } = useBrainStore.getState();
  return Object.values(marketingCampaigns)
    .filter((c) => c.channel === 'meta')
    .reduce((sum, c) => sum + c.spendAud, 0);
}

/** The acquisition funnel, ad spend down to completed bookings, over the window. */
export function funnel(days: readonly string[], studioId?: StudioId | null): FunnelStep[] {
  const { customers } = useBrainStore.getState();
  const set = new Set(days);
  let inquiries = 0;
  let qualifiedLeads = 0;
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    if (!set.has(dayKeyOf(c.source.firstTouchAt))) continue;
    inquiries += 1;
    if (qualified(c)) qualifiedLeads += 1;
  }
  const depositsPaid = depositsPaidByDay(days, studioId).reduce((s, d) => s + d.count, 0);
  const completed = weeklyCompletedTally(days, studioId);
  return [
    { stage: 'Meta ad spend', value: metaAdSpend(), kind: 'spend' },
    { stage: 'Inquiries', value: inquiries, kind: 'count' },
    { stage: 'Qualified leads', value: qualifiedLeads, kind: 'count' },
    { stage: 'Deposits paid', value: depositsPaid, kind: 'count' },
    { stage: 'Bookings completed', value: completed, kind: 'count' },
  ];
}

// --- Campaigns -------------------------------------------------------------

export interface CampaignRow {
  campaign: MarketingCampaign;
  messagesSent: number;
  deposits: number;
  spendAud: number;
  /** spend / deposits attributed; null when none converted yet. */
  costPerDepositAud: number | null;
}

/** Deposits attributed to a campaign via the paying customer's `source.campaignId`. */
function paidDepositsByCampaign(): Record<string, number> {
  const { deposits, customers } = useBrainStore.getState();
  const out: Record<string, number> = {};
  for (const dep of Object.values(deposits)) {
    if (dep.status !== 'paid') continue;
    const campaignId = customers[dep.customerId]?.source.campaignId;
    if (!campaignId) continue;
    out[campaignId] = (out[campaignId] ?? 0) + 1;
  }
  return out;
}

/** Per-campaign cost-per-booked-deposit (seeded spend / deposits attributed). */
export function campaignPerformance(): CampaignRow[] {
  const { marketingCampaigns } = useBrainStore.getState();
  const depositsByCampaign = paidDepositsByCampaign();
  return Object.values(marketingCampaigns).map((campaign) => {
    const deposits = depositsByCampaign[campaign.id] ?? 0;
    return {
      campaign,
      messagesSent: campaign.messagesSent,
      deposits,
      spendAud: campaign.spendAud,
      costPerDepositAud: deposits > 0 ? campaign.spendAud / deposits : null,
    };
  });
}

// --- Per-studio summary ----------------------------------------------------

export interface StudioSummaryRow {
  studioId: StudioId;
  name: string;
  inquiries: number;
  deposits: number;
  /** deposits / inquiries; 0 when no inquiries. */
  conversion: number;
  /** targeted campaign spend / deposits; null when no deposits. */
  costPerDepositAud: number | null;
}

/** The live replacement for the fake per-studio table. */
export function perStudioSummary(days: readonly string[]): StudioSummaryRow[] {
  const { studios, marketingCampaigns } = useBrainStore.getState();
  const spendByStudio: Record<string, number> = {};
  for (const c of Object.values(marketingCampaigns)) {
    if (!c.studioId) continue;
    spendByStudio[c.studioId] = (spendByStudio[c.studioId] ?? 0) + c.spendAud;
  }
  return Object.values(studios).map((s) => {
    const inquiries = newLeadsByDay(days, s.id).reduce((sum, d) => sum + d.count, 0);
    const deposits = depositsPaidByDay(days, s.id).reduce((sum, d) => sum + d.count, 0);
    const spend = spendByStudio[s.id] ?? 0;
    return {
      studioId: s.id,
      name: s.name,
      inquiries,
      deposits,
      conversion: inquiries > 0 ? deposits / inquiries : 0,
      costPerDepositAud: deposits > 0 && spend > 0 ? spend / deposits : null,
    };
  });
}

// --- KPI tiles -------------------------------------------------------------

export interface KpiTiles {
  /**
   * Revenue / bookings completed for the window: the mean session gross
   * (`sessionGrossAud`, the same number Revenue sums) across completed sessions.
   */
  avgBookingValueAud: number;
  /** Returning customers / customers with any completed session (all-time). */
  retentionRate: number;
  /** No-shows / (completed + no-show) sessions in the window. */
  noShowRate: number;
}

export function kpiTiles(days: readonly string[], studioId?: StudioId | null): KpiTiles {
  const { bookings, salePayments, artists } = useBrainStore.getState();
  const set = new Set(days);
  const salesByBooking = salesByBookingId(salePayments);

  let completedValue = 0;
  let completedCount = 0;
  let noShow = 0;
  // ASSUMPTION: retention = share of customers with a completed session who have
  // more than one. A placeholder definition until the client specifies theirs.
  const completedByCustomer = new Map<string, number>();

  for (const b of Object.values(bookings)) {
    if (!inStudio(studioId, b.studioId)) continue;
    if (b.status === 'completed') {
      completedByCustomer.set(b.customerId, (completedByCustomer.get(b.customerId) ?? 0) + 1);
      if (set.has(completedDayKey(b))) {
        // Same gross as Revenue (sale ledger, else the booking) so avg = revenue / completed.
        completedValue += sessionGrossAud(b, salesByBooking, artists);
        completedCount += 1;
      }
    } else if (b.status === 'no-show' && set.has(dayKeyOf(b.start))) {
      noShow += 1;
    }
  }

  const returning = [...completedByCustomer.values()].filter((n) => n > 1).length;
  const withCompleted = completedByCustomer.size;

  return {
    avgBookingValueAud: completedCount > 0 ? completedValue / completedCount : 0,
    retentionRate: withCompleted > 0 ? returning / withCompleted : 0,
    noShowRate: completedCount + noShow > 0 ? noShow / (completedCount + noShow) : 0,
  };
}

// --- Lapsed regulars (Customers tab, Stage 5) ------------------------------

/**
 * Customers whose gap since their last completed session exceeds 1.5x their own
 * historical average gap - "came every six weeks, now eight". The reactivation
 * engine feeds on this count.
 */
export function lapsedRegularsCount(studioId?: StudioId | null): number {
  const { bookings, customers } = useBrainStore.getState();
  const now = new Date(reportingNow()).getTime();

  const completedByCustomer = new Map<string, number[]>();
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed') continue;
    if (!inStudio(studioId, b.studioId)) continue;
    const at = new Date(b.checkedOutAt ?? b.paidAt ?? b.start).getTime();
    const list = completedByCustomer.get(b.customerId) ?? [];
    list.push(at);
    completedByCustomer.set(b.customerId, list);
  }

  let lapsed = 0;
  for (const [customerId, timesRaw] of completedByCustomer) {
    if (!customers[customerId]) continue;
    const times = [...timesRaw].sort((a, b) => a - b);
    if (times.length < 2) continue; // need a history to have an average gap
    const gaps: number[] = [];
    for (let i = 1; i < times.length; i += 1) gaps.push(times[i]! - times[i - 1]!);
    const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    const sinceLast = now - times[times.length - 1]!;
    if (avgGap > 0 && sinceLast > 1.5 * avgGap) lapsed += 1;
  }
  return lapsed;
}

// --- Targets board (Stage 3) ----------------------------------------------

/** Thursday-start day-of-week (0=Sun). The target week runs Thu..Tue. */
export const TARGET_WEEK_START_DOW = 4;
/** Six days Thu..Tue; Wednesday is the review day (end of the target week). */
export const TARGET_WEEK_LENGTH = 6;

export interface TargetWeek {
  /** 'YYYY-MM-DD' of the week's Thursday. */
  thursday: string;
  /** The six day-keys Thu..Tue. */
  days: string[];
  /** 'YYYY-MM-DD' of the Wednesday review day. */
  reviewDay: string;
}

/**
 * The target week `now` falls in. Thu->Tue are the six working days; Wednesday is
 * the review day and belongs to the week that just ended. Transcript-corrected:
 * "the week starts on a Thursday and ends on a Tuesday... Wednesday's our end of
 * target week" (the proposal's Mon-Wed wording is wrong).
 */
export function targetWeek(now: string = reportingNow()): TargetWeek {
  const anchor = new Date(`${dayKeyOf(now)}T12:00:00Z`);
  const daysSinceThursday = (anchor.getUTCDay() - TARGET_WEEK_START_DOW + 7) % 7;
  const thursday = new Date(anchor.getTime() - daysSinceThursday * 24 * 60 * 60_000);
  const dayAt = (offset: number) =>
    new Date(thursday.getTime() + offset * 24 * 60 * 60_000).toISOString().slice(0, 10);
  return {
    thursday: dayAt(0),
    days: Array.from({ length: TARGET_WEEK_LENGTH }, (_, i) => dayAt(i)),
    reviewDay: dayAt(TARGET_WEEK_LENGTH), // the Wednesday review
  };
}

export interface UnworkedLeads {
  count: number;
  /** First-touch ISO of the oldest untouched lead, if any. */
  oldestIso?: string;
}

/**
 * Leads sitting untouched - an active lead (new/qualifying) the team has never
 * reached out to (no outbound conversation entry). The Collingwood 20% miss was
 * leads "literally sitting there... it just hadn't been done"; this is the early
 * warning tile on the Targets board.
 */
export function unworkedLeads(studioId?: StudioId | null): UnworkedLeads {
  const { customers } = useBrainStore.getState();
  let count = 0;
  let oldestIso: string | undefined;
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    if (c.leadStage !== 'new' && c.leadStage !== 'qualifying') continue;
    if (c.conversation.some((e) => e.direction === 'out')) continue; // already worked
    count += 1;
    if (!oldestIso || c.source.firstTouchAt < oldestIso) oldestIso = c.source.firstTouchAt;
  }
  return { count, ...(oldestIso ? { oldestIso } : {}) };
}

/**
 * What a session is worth: the checkout sale where one exists, else the amount
 * recorded as paid on the booking, else (B1) its booked price — the same rule the
 * payout envelopes use (@atink/domain sessionTakingsAud), so Reporting, the Day
 * sheet and Payouts never disagree. Deliberately excludes merch and adjustments.
 *
 * Exported because every surface that shows "what the customer was charged" must
 * read the same number. Pass `artists` so a session with no money record falls
 * back to the rate card; without it the fallback is the booking's day rate.
 */
export function sessionGrossAud(
  b: Booking,
  salesByBooking: Map<string, SalePayment>,
  artists: Record<string, Partial<Pick<Artist, 'rates'>> | undefined> = {},
): number {
  // B1: the ONE session price rule, shared with the payout envelopes.
  return sessionTakingsFor(b, salesByBooking, artists);
}

export interface TargetCell {
  dayIso: string;
  sessions: number;
  dollarsAud: number;
  /** Whether this day has elapsed relative to now. */
  elapsed: boolean;
  /** An elapsed day meeting its per-day session target. Future days are neutral. */
  onPace: boolean;
}

export interface TargetArtistRow {
  artistId: string;
  name: string;
  cells: TargetCell[];
  sessions: number;
  dollarsAud: number;
  /** dollars / sessions; 0 when no sessions. The $20k-pattern catcher. */
  avgJobValueAud: number;
  targetSessions: number;
  targetDollarsAud: number;
  expectedSessionsByNow: number;
  expectedDollarsByNow: number;
  sessionsOnPace: boolean;
  dollarsOnPace: boolean;
}

export interface TargetBoard {
  studioId: StudioId;
  thursday: string;
  reviewDayIso: string;
  days: string[];
  daysElapsed: number;
  daysTotal: number;
  rows: TargetArtistRow[];
  totals: {
    sessions: number;
    dollarsAud: number;
    targetSessions: number;
    targetDollarsAud: number;
    expectedSessionsByNow: number;
    expectedDollarsByNow: number;
  };
  unworkedLeads: UnworkedLeads;
}

/**
 * The live replacement for the ops team's hand-built per-studio target sheet.
 * Rows = the studio's active artists, columns = the six target-week days, each
 * cell the day's sessions + dollars (red/green vs a pro-rata per-day target),
 * plus an average-job-value column and the elapsed-days "should be at X" tally.
 */
/** One artist's weekly target, read live from the editable store slice (Settings). */
export function artistTarget(artistId: string): ArtistWeeklyTarget {
  const { reportingTargets } = useBrainStore.getState();
  return reportingTargets.artistOverrides[artistId] ?? reportingTargets.studioDefault;
}

/** A studio's weekly reviews target (per-studio override, else the default). */
export function studioReviewsTarget(studioId: StudioId): number {
  const { reportingTargets } = useBrainStore.getState();
  return reportingTargets.reviewsByStudio[studioId] ?? reportingTargets.reviewsDefault;
}

/** A studio's deposit-sessions target = the sum of its active artists' targets. */
export function studioDepositSessionsTarget(studioId: StudioId): number {
  const { artists, reportingTargets } = useBrainStore.getState();
  let total = 0;
  for (const a of Object.values(artists)) {
    if (a.archived || a.studioId !== studioId) continue;
    total += (reportingTargets.artistOverrides[a.id] ?? reportingTargets.studioDefault).depositSessionsPerWeek;
  }
  return total;
}

export function targetBoard(studioId: StudioId, now: string = reportingNow()): TargetBoard {
  const { bookings, salePayments, artists } = useBrainStore.getState();
  const week = targetWeek(now);
  const daySet = new Set(week.days);
  const nowKey = dayKeyOf(now);
  const daysElapsed = week.days.filter((d) => d <= nowKey).length;
  const daysTotal = week.days.length;
  const paceFraction = daysTotal > 0 ? daysElapsed / daysTotal : 0;

  const salesByBooking = salesByBookingId(salePayments);

  // artistId -> dayKey -> day totals
  const totalsByArtist = new Map<string, Map<string, { sessions: number; dollarsAud: number }>>();
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed' || b.studioId !== studioId) continue;
    const day = completedDayKey(b);
    if (!daySet.has(day)) continue;
    const perDay = totalsByArtist.get(b.artistId) ?? new Map<string, { sessions: number; dollarsAud: number }>();
    const cell = perDay.get(day) ?? { sessions: 0, dollarsAud: 0 };
    cell.sessions += 1;
    cell.dollarsAud += sessionGrossAud(b, salesByBooking, artists);
    perDay.set(day, cell);
    totalsByArtist.set(b.artistId, perDay);
  }

  const studioArtists = Object.values(artists)
    .filter((a) => a.studioId === studioId && !a.archived)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const rows: TargetArtistRow[] = studioArtists.map((artist) => {
    const perDay = totalsByArtist.get(artist.id);
    const target = artistTarget(artist.id);
    const perDayTargetSessions = target.sessionsPerWeek / daysTotal;

    const cells: TargetCell[] = week.days.map((dayIso) => {
      const t = perDay?.get(dayIso) ?? { sessions: 0, dollarsAud: 0 };
      const elapsed = dayIso <= nowKey;
      return {
        dayIso,
        sessions: t.sessions,
        dollarsAud: t.dollarsAud,
        elapsed,
        onPace: elapsed && t.sessions >= perDayTargetSessions,
      };
    });

    const sessions = cells.reduce((s, c) => s + c.sessions, 0);
    const dollarsAud = cells.reduce((s, c) => s + c.dollarsAud, 0);
    const expectedSessionsByNow = target.sessionsPerWeek * paceFraction;
    const expectedDollarsByNow = target.dollarsPerWeek * paceFraction;

    return {
      artistId: artist.id,
      name: artist.displayName,
      cells,
      sessions,
      dollarsAud,
      avgJobValueAud: sessions > 0 ? dollarsAud / sessions : 0,
      targetSessions: target.sessionsPerWeek,
      targetDollarsAud: target.dollarsPerWeek,
      expectedSessionsByNow,
      expectedDollarsByNow,
      sessionsOnPace: sessions >= expectedSessionsByNow,
      dollarsOnPace: dollarsAud >= expectedDollarsByNow,
    };
  });

  const totals = rows.reduce(
    (acc, r) => ({
      sessions: acc.sessions + r.sessions,
      dollarsAud: acc.dollarsAud + r.dollarsAud,
      targetSessions: acc.targetSessions + r.targetSessions,
      targetDollarsAud: acc.targetDollarsAud + r.targetDollarsAud,
      expectedSessionsByNow: acc.expectedSessionsByNow + r.expectedSessionsByNow,
      expectedDollarsByNow: acc.expectedDollarsByNow + r.expectedDollarsByNow,
    }),
    {
      sessions: 0,
      dollarsAud: 0,
      targetSessions: 0,
      targetDollarsAud: 0,
      expectedSessionsByNow: 0,
      expectedDollarsByNow: 0,
    },
  );

  return {
    studioId,
    thursday: week.thursday,
    reviewDayIso: week.reviewDay,
    days: week.days,
    daysElapsed,
    daysTotal,
    rows,
    totals,
    unworkedLeads: unworkedLeads(studioId),
  };
}

// --- F4: Leads & marketing -------------------------------------------------

/** Coarse acquisition-source group for the leads chart: paid campaign, social DM, or web/direct. */
export type LeadSourceGroup = 'web' | 'social' | 'campaign';

/** Bucket a customer by how they arrived. A campaign attribution wins over the raw channel. */
export function leadSourceGroup(c: CustomerRecord): LeadSourceGroup {
  if (c.source.campaignId) return 'campaign';
  if (c.source.channel === 'meta-dm' || c.source.channel === 'ig-dm') return 'social';
  return 'web';
}

export interface LeadSourceDay {
  dayIso: string;
  web: number;
  social: number;
  campaign: number;
}

/** New leads per day split into the web / social / campaign source groups. */
export function newLeadsBySourceByDay(days: readonly string[], studioId?: StudioId | null): LeadSourceDay[] {
  const { customers } = useBrainStore.getState();
  const set = new Set(days);
  const perDay = new Map<string, LeadSourceDay>(
    days.map((d) => [d, { dayIso: d, web: 0, social: 0, campaign: 0 }]),
  );
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    const key = dayKeyOf(c.source.firstTouchAt);
    if (!set.has(key)) continue;
    perDay.get(key)![leadSourceGroup(c)] += 1;
  }
  return days.map((d) => perDay.get(d)!);
}

/** Local wall-clock hour (0..23) of an ISO instant. Seeds carry +10:00, so the slice is the intended local hour. */
function localHourOf(iso: string): number {
  return Number.parseInt(iso.slice(11, 13), 10) || 0;
}

/** Day-of-week (0=Sun..6=Sat) of a 'YYYY-MM-DD' calendar-day key. */
function dowOf(dayKey: string): number {
  return new Date(`${dayKey}T12:00:00Z`).getUTCDay();
}

export interface InquiryArrivalRow {
  /** 0=Sun..6=Sat. */
  dow: number;
  label: string;
  /** First-touch count per hour, indexed 0..23. */
  hours: number[];
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Monday-first row order so the heatmap reads like a working week. */
const WEEK_ROW_ORDER = [1, 2, 3, 4, 5, 6, 0];

/**
 * Inquiry-arrival matrix: first-touch counts by weekday x hour, all-time. The
 * staffing signal - when do inquiries actually land - so the roster can meet
 * them. One row per weekday (Monday first), 24 hour buckets each.
 */
export function inquiryArrivalHeatmap(studioId?: StudioId | null): InquiryArrivalRow[] {
  const { customers } = useBrainStore.getState();
  const byDow = new Map<number, number[]>(WEEK_ROW_ORDER.map((d) => [d, new Array(24).fill(0)]));
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    const iso = c.source.firstTouchAt;
    const row = byDow.get(dowOf(dayKeyOf(iso)))!;
    const hour = localHourOf(iso);
    row[hour] = (row[hour] ?? 0) + 1;
  }
  return WEEK_ROW_ORDER.map((dow) => ({ dow, label: WEEKDAY_LABELS[dow]!, hours: byDow.get(dow)! }));
}

// --- F4: Calls & agents / queue health -------------------------------------

export interface QueueHealth {
  studioId: StudioId;
  name: string;
  /** Actionable items in the studio's live operator queue (the main list). */
  depth: number;
  /** ISO of the oldest actionable item, if any. */
  oldestIso?: string;
}

/**
 * Live operator-queue health per studio - the depth (how many actionable items
 * are waiting) and the oldest one. Reads the same `buildQueue` the operator
 * screen renders, scoped per studio, so the numbers match what agents see.
 */
export function queueHealthByStudio(now: string = reportingNow(), studioId?: StudioId | null): QueueHealth[] {
  const { customers, bookings, deposits, pieces, artists, rebookFollowupTasks, sequenceState, studios } =
    useBrainStore.getState();
  return Object.values(studios)
    .filter((s) => !s.archived)
    .filter((s) => !studioId || s.id === studioId)
    .map((s) => {
      const items = buildQueue({
        customers,
        bookings,
        deposits,
        pieces,
        artists,
        rebookFollowupTasks,
        studioIds: [s.id],
        nowIso: now,
        sequenceState,
      });
      const { main } = partitionQueue(items);
      let oldestIso: string | undefined;
      for (const it of main) {
        if (it.recommendedAt && (!oldestIso || it.recommendedAt < oldestIso)) oldestIso = it.recommendedAt;
      }
      return { studioId: s.id, name: s.name, depth: main.length, ...(oldestIso ? { oldestIso } : {}) };
    });
}

// --- F4: Rebooks & artists -------------------------------------------------

export interface RebookRateDay {
  dayIso: string;
  rebooks: number;
  completed: number;
  /** That day's rebook rate as a percentage; null when nothing completed that day. */
  ratePct: number | null;
  /** Running rebook rate across the window up to and including this day; null until any completion. */
  cumulativePct: number | null;
}

/**
 * Per-day rebook rate across the window, plus a cumulative running rate. The
 * cumulative line is what the Rebooks tab plots against the 50% baseline and the
 * 40% manager target - it converges smoothly rather than jumping on sparse days.
 */
export function rebookRateByDay(days: readonly string[], studioId?: StudioId | null): RebookRateDay[] {
  const { bookings, pieces } = useBrainStore.getState();
  const set = new Set(days);
  const perDay = new Map<string, { rebooks: number; completed: number }>(
    days.map((d) => [d, { rebooks: 0, completed: 0 }]),
  );
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed') continue;
    if (!inStudio(studioId, b.studioId)) continue;
    const key = completedDayKey(b);
    if (!set.has(key)) continue;
    if (isEndOfWorkNoRebook(b, pieces)) continue;
    const cell = perDay.get(key)!;
    cell.completed += 1;
    if (rebookedInChair(b)) cell.rebooks += 1;
  }
  let cumRebooks = 0;
  let cumCompleted = 0;
  return days.map((d) => {
    const { rebooks, completed } = perDay.get(d)!;
    cumRebooks += rebooks;
    cumCompleted += completed;
    return {
      dayIso: d,
      rebooks,
      completed,
      ratePct: completed > 0 ? (rebooks / completed) * 100 : null,
      cumulativePct: cumCompleted > 0 ? (cumRebooks / cumCompleted) * 100 : null,
    };
  });
}

export interface StoreRebookDays {
  studioId: StudioId;
  name: string;
  perDay: DayCount[];
}

/** Rebooks per store per day across the window - the stores x days heatmap source. Scoped to one studio when given. */
export function rebooksByStoreDay(days: readonly string[], studioId?: StudioId | null): StoreRebookDays[] {
  const { bookings, studios } = useBrainStore.getState();
  const set = new Set(days);
  const byStudioDay = new Map<string, Map<string, number>>();
  for (const b of Object.values(bookings)) {
    if (!isRebook(b)) continue;
    if (!inStudio(studioId, b.studioId)) continue;
    const key = rebookDayKey(b);
    if (!set.has(key)) continue;
    const perDay = byStudioDay.get(b.studioId) ?? new Map<string, number>();
    perDay.set(key, (perDay.get(key) ?? 0) + 1);
    byStudioDay.set(b.studioId, perDay);
  }
  return Object.values(studios)
    .filter((s) => !s.archived)
    .filter((s) => !studioId || s.id === studioId)
    .map((s) => {
      const perDay = byStudioDay.get(s.id);
      return {
        studioId: s.id,
        name: s.name,
        perDay: days.map((d) => ({ dayIso: d, count: perDay?.get(d) ?? 0 })),
      };
    });
}

/** Piece statuses that count as finished work. Shared so every surface agrees. */
export const COMPLETED_PIECE_STATUSES = new Set(['completed', 'single-piece-done']);
const ABANDONED_PIECE_STATUSES = new Set(['abandoned', 'abandoned-large']);
const OPEN_PIECE_STATUSES = new Set(['in-progress', 'paused-will-return']);

export interface ArtistQualityRow {
  artistId: string;
  name: string;
  studioId: StudioId;
  /** Mean gross across the artist's completed sessions (all-time). */
  avgJobValueAud: number;
  completedSessions: number;
  /** Share of the artist's customers (with a completed session) who came back for more than one. */
  repeatRate: number;
  /** Completed / (completed + abandoned) terminal pieces; null when none have reached an outcome. */
  pieceCompletionRate: number | null;
  /** Remaining estimated sessions across open pieces (estimated minus completed) - the locked-06-Jul signal. */
  openSessionsRemaining: number;
}

/**
 * Per-artist quality mini-table, ranked by estimated-minus-completed open
 * sessions (descending) - the artist whose booked work is dragging sits on top.
 * Average job value, repeat-booking rate and piece-completion rate round out the
 * quality picture. All-time (not window-scoped) so the ranking is stable.
 */
export function artistQuality(studioId?: StudioId | null): ArtistQualityRow[] {
  const { bookings, salePayments, pieces, artists } = useBrainStore.getState();
  const salesByBooking = salesByBookingId(salePayments);

  // Per-artist completed-session gross + which customers returned.
  const grossByArtist = new Map<string, number>();
  const sessionsByArtist = new Map<string, number>();
  const customerSessionsByArtist = new Map<string, Map<string, number>>();
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed') continue;
    grossByArtist.set(b.artistId, (grossByArtist.get(b.artistId) ?? 0) + sessionGrossAud(b, salesByBooking, artists));
    sessionsByArtist.set(b.artistId, (sessionsByArtist.get(b.artistId) ?? 0) + 1);
    const byCust = customerSessionsByArtist.get(b.artistId) ?? new Map<string, number>();
    byCust.set(b.customerId, (byCust.get(b.customerId) ?? 0) + 1);
    customerSessionsByArtist.set(b.artistId, byCust);
  }

  // Per-artist piece tallies.
  const completedPieces = new Map<string, number>();
  const decidedPieces = new Map<string, number>();
  const openRemaining = new Map<string, number>();
  for (const p of Object.values(pieces)) {
    const a = p.assignedArtistId;
    if (!a) continue;
    const done = COMPLETED_PIECE_STATUSES.has(p.status);
    if (done || ABANDONED_PIECE_STATUSES.has(p.status)) {
      decidedPieces.set(a, (decidedPieces.get(a) ?? 0) + 1);
      if (done) completedPieces.set(a, (completedPieces.get(a) ?? 0) + 1);
    }
    if (OPEN_PIECE_STATUSES.has(p.status)) {
      const remaining = Math.max(0, (p.estimatedTotalSessions ?? 0) - (p.sessionsCompleted ?? 0));
      openRemaining.set(a, (openRemaining.get(a) ?? 0) + remaining);
    }
  }

  return Object.values(artists)
    .filter((a) => !a.archived)
    .filter((a) => !studioId || a.studioId === studioId)
    .filter((a) => (sessionsByArtist.get(a.id) ?? 0) > 0 || (decidedPieces.get(a.id) ?? 0) > 0 || (openRemaining.get(a.id) ?? 0) > 0)
    .map((a) => {
      const completedSessions = sessionsByArtist.get(a.id) ?? 0;
      const gross = grossByArtist.get(a.id) ?? 0;
      const byCust = customerSessionsByArtist.get(a.id);
      const custCount = byCust?.size ?? 0;
      const repeatCount = byCust ? [...byCust.values()].filter((n) => n > 1).length : 0;
      const decided = decidedPieces.get(a.id) ?? 0;
      return {
        artistId: a.id,
        name: a.displayName,
        studioId: a.studioId,
        avgJobValueAud: completedSessions > 0 ? gross / completedSessions : 0,
        completedSessions,
        repeatRate: custCount > 0 ? repeatCount / custCount : 0,
        pieceCompletionRate: decided > 0 ? (completedPieces.get(a.id) ?? 0) / decided : null,
        openSessionsRemaining: openRemaining.get(a.id) ?? 0,
      };
    })
    .sort((x, y) => y.openSessionsRemaining - x.openSessionsRemaining || x.name.localeCompare(y.name));
}

// --- F5: Money -------------------------------------------------------------

export interface DayRevenue {
  dayIso: string;
  oursAud: number;
  artistAud: number;
  totalAud: number;
}

/** Revenue aggregated per day across studios (or one studio), ours vs artist split from E2. */
export function revenueByDay(days: readonly string[], studioId?: StudioId | null): DayRevenue[] {
  const perDay = new Map<string, DayRevenue>(
    days.map((d) => [d, { dayIso: d, oursAud: 0, artistAud: 0, totalAud: 0 }]),
  );
  for (const r of revenueByStoreDay(days, studioId)) {
    const cell = perDay.get(r.dayIso);
    if (!cell) continue;
    cell.oursAud += r.oursAud;
    cell.artistAud += r.artistAud;
    cell.totalAud += r.totalAud;
  }
  return days.map((d) => perDay.get(d)!);
}

export interface StoreRevenueTotals {
  studioId: StudioId;
  name: string;
  oursAud: number;
  artistAud: number;
  totalAud: number;
}

/**
 * Per-store revenue totals across the window - the per-store totals row Zack
 * asked for (08 Jul) inside the reporting screen. Every active studio appears,
 * zero-filled, so the row is a stable roll-call; largest total first.
 */
export function revenueByStore(days: readonly string[], studioId?: StudioId | null): StoreRevenueTotals[] {
  const { studios } = useBrainStore.getState();
  const byStore = new Map<string, { oursAud: number; artistAud: number; totalAud: number }>();
  for (const r of revenueByStoreDay(days, studioId)) {
    const cell = byStore.get(r.studioId) ?? { oursAud: 0, artistAud: 0, totalAud: 0 };
    cell.oursAud += r.oursAud;
    cell.artistAud += r.artistAud;
    cell.totalAud += r.totalAud;
    byStore.set(r.studioId, cell);
  }
  return Object.values(studios)
    .filter((s) => !s.archived)
    .filter((s) => !studioId || s.id === studioId)
    .map((s) => ({
      studioId: s.id,
      name: s.name,
      ...(byStore.get(s.id) ?? { oursAud: 0, artistAud: 0, totalAud: 0 }),
    }))
    .sort((a, b) => b.totalAud - a.totalAud || a.name.localeCompare(b.name));
}

/**
 * The E3 cash-timing strip aggregated across all active studios, for the Money
 * tab's reporting-level view. Reuses E3's per-studio `cashTimingStrip` (no new
 * settlement logic) and sums each day. The per-studio detail lives on /calculations.
 */
export function cashTimingAcrossStudios(
  endDayIso: string = reportingNow(),
  studioId?: StudioId | null,
): TimingStripDay[] {
  const { studios } = useBrainStore.getState();
  const days = reconWeekDays(endDayIso);
  const byDay = new Map<string, TimingStripDay>(
    days.map((d) => {
      const dow = weekdayOf(d);
      return [d, { dayIso: d, takingsAud: 0, landingAud: 0, weekendGap: dow === 0 || dow === 6 }];
    }),
  );
  for (const s of Object.values(studios).filter((st) => !st.archived && (!studioId || st.id === studioId))) {
    for (const d of cashTimingStrip(s.id, endDayIso)) {
      const cell = byDay.get(d.dayIso);
      if (!cell) continue;
      cell.takingsAud += d.takingsAud;
      cell.landingAud += d.landingAud;
    }
  }
  return days.map((d) => byDay.get(d)!);
}

/**
 * Merch/aftercare sold across the given day window, aggregated across studios
 * (or one) and rolled up per item. "Money earned, all house" - reuses E3's
 * per-studio `merchBreakdown` so the reporting figure matches the money reports.
 */
export function merchSold(days: readonly string[], studioId?: StudioId | null): { totalAud: number; lines: MerchBreakdownRow[] } {
  const { studios } = useBrainStore.getState();
  const byItem = new Map<string, MerchBreakdownRow>();
  for (const s of Object.values(studios).filter((st) => !st.archived && (!studioId || st.id === studioId))) {
    for (const day of days) {
      for (const line of merchBreakdown(s.id, day)) {
        const existing = byItem.get(line.itemId);
        if (existing) {
          existing.qty += line.qty;
          existing.totalAud += line.totalAud;
        } else {
          byItem.set(line.itemId, { ...line });
        }
      }
    }
  }
  const lines = [...byItem.values()].sort((a, b) => b.totalAud - a.totalAud);
  return { totalAud: lines.reduce((sum, l) => sum + l.totalAud, 0), lines };
}

/**
 * New money in via deposits across the given day window, aggregated across
 * studios (or one) - the money-in view (not revenue). Reuses E3's per-studio
 * `depositsReceivedBreakdown`; each row is one deposit money-in event.
 */
export function depositsReceivedIn(days: readonly string[], studioId?: StudioId | null): { totalAud: number; rows: DepositReceivedRow[] } {
  const { studios } = useBrainStore.getState();
  const rows: DepositReceivedRow[] = [];
  for (const s of Object.values(studios).filter((st) => !st.archived && (!studioId || st.id === studioId))) {
    for (const day of days) rows.push(...depositsReceivedBreakdown(s.id, day));
  }
  rows.sort((a, b) => b.amountAud - a.amountAud);
  return { totalAud: rows.reduce((sum, r) => sum + r.amountAud, 0), rows };
}

// --- Money report: trends, per-artist drill, pool, period deltas -----------

/**
 * Deposits received per day across the window, aggregated across studios (or one).
 * Reuses E3's `depositsReceivedBreakdown` so it reconciles with `depositsReceivedIn`
 * (excludes checkout rollovers). Powers the money-in mini-trend on the Money tab.
 */
export function depositsReceivedByDay(days: readonly string[], studioId?: StudioId | null): DayMoney[] {
  const { studios } = useBrainStore.getState();
  const perDay = new Map<string, { count: number; amountAud: number }>(
    days.map((d) => [d, { count: 0, amountAud: 0 }]),
  );
  for (const s of Object.values(studios).filter((st) => !st.archived && (!studioId || st.id === studioId))) {
    for (const day of days) {
      const cell = perDay.get(day)!;
      for (const r of depositsReceivedBreakdown(s.id, day)) {
        cell.count += 1;
        cell.amountAud += r.amountAud;
      }
    }
  }
  return days.map((d) => ({ dayIso: d, ...perDay.get(d)! }));
}

/**
 * Merch/aftercare sold per day across the window, aggregated across studios (or
 * one). Reuses E3's `merchBreakdown` so it reconciles with `merchSold`. Powers the
 * merch mini-trend on the Money tab.
 */
export function merchSoldByDay(days: readonly string[], studioId?: StudioId | null): DayMoney[] {
  const { studios } = useBrainStore.getState();
  const perDay = new Map<string, { count: number; amountAud: number }>(
    days.map((d) => [d, { count: 0, amountAud: 0 }]),
  );
  for (const s of Object.values(studios).filter((st) => !st.archived && (!studioId || st.id === studioId))) {
    for (const day of days) {
      const cell = perDay.get(day)!;
      for (const line of merchBreakdown(s.id, day)) {
        cell.count += line.qty;
        cell.amountAud += line.totalAud;
      }
    }
  }
  return days.map((d) => ({ dayIso: d, ...perDay.get(d)! }));
}

export interface MerchCategoryTotal {
  category: MerchBreakdownRow['category'];
  qty: number;
  totalAud: number;
}

/** Merch sold in the window grouped by category, largest first - the by-category bars. */
export function merchByCategory(days: readonly string[], studioId?: StudioId | null): MerchCategoryTotal[] {
  const byCat = new Map<string, MerchCategoryTotal>();
  for (const line of merchSold(days, studioId).lines) {
    const cell = byCat.get(line.category) ?? { category: line.category, qty: 0, totalAud: 0 };
    cell.qty += line.qty;
    cell.totalAud += line.totalAud;
    byCat.set(line.category, cell);
  }
  return [...byCat.values()].sort((a, b) => b.totalAud - a.totalAud);
}

export interface ArtistRevenueTotals {
  artistId: string;
  name: string;
  sessions: number;
  oursAud: number;
  artistAud: number;
  totalAud: number;
}

/**
 * Per-artist revenue for a single studio over the window - the drill the Money
 * tab's per-store table swaps to when one studio is selected, so it stops just
 * repeating the chart's single total. Same gross + commission-split rule as
 * `revenueByStoreDay`. Largest turnover first.
 */
export function revenueByArtist(days: readonly string[], studioId: StudioId): ArtistRevenueTotals[] {
  const { artists } = useBrainStore.getState();
  const byArtist = new Map<string, ArtistRevenueTotals>();
  for (const g of artistDaySplits(days, (b) => b.studioId === studioId)) {
    const cell = byArtist.get(g.artistId) ?? {
      artistId: g.artistId,
      name: artists[g.artistId]?.displayName ?? g.artistId,
      sessions: 0,
      oursAud: 0,
      artistAud: 0,
      totalAud: 0,
    };
    cell.sessions += g.sessions;
    cell.oursAud += g.oursAud;
    cell.artistAud += g.artistAud;
    cell.totalAud += g.grossAud;
    byArtist.set(g.artistId, cell);
  }
  return [...byArtist.values()].sort((a, b) => b.totalAud - a.totalAud || a.name.localeCompare(b.name));
}

/**
 * The equally-long day window immediately before `days` (same length, ending the
 * day before `days` starts) - the comparison period for the Money tab's deltas.
 * Empty when `days` is empty.
 */
export function previousWindowDays(days: readonly string[]): string[] {
  if (days.length === 0) return [];
  const prevEnd = new Date(`${days[0]!}T12:00:00Z`).getTime() - 86_400_000;
  return windowDays(new Date(prevEnd).toISOString().slice(0, 10), days.length);
}

export type DepositAgingKey = 'pastValidity' | 'le30' | 'd31to90' | 'd91plus';

export interface DepositAgingBucket {
  key: DepositAgingKey;
  amountAud: number;
  count: number;
}

export interface DepositPool {
  /** Money currently held as unredeemed deposits - a liability, not revenue. */
  heldAud: number;
  count: number;
  /** Held money on a live booking (held, confirmed, on the day): allocated work, never aged. */
  bookedInAud: number;
  bookedInCount: number;
  /** Held money with no booking at all: on file, the Sweeper's "deposit, no date". */
  onFileAud: number;
  onFileCount: number;
  /** @deprecated the old name for `onFileAud` (legacy reporting screen). */
  floatingAud: number;
  /** @deprecated the old name for `onFileCount`. */
  floatingCount: number;
  /** Held money still linked only to a cancelled / no-show / moved booking: needs a decision. */
  needsDecisionAud: number;
  needsDecisionCount: number;
  /**
   * The ON-FILE money only, bucketed by time left on its 12-month validity
   * (`onFileValidUntil`); past validity first. Sums to `onFileAud`.
   */
  aging: DepositAgingBucket[];
  /** Deposits that expired/were forfeited within `rangeDays` - money moved to house. */
  forfeitedInRangeAud: number;
  forfeitedInRangeCount: number;
}

/**
 * The live deposit-liability snapshot (B10 / 14ym9bv7652). A *now* figure;
 * `rangeDays` only scopes the forfeited-in-range line. One shared rule
 * (@atink/domain `depositStanding`) splits the held money:
 *   - booked in (a live booking) — one line, never aged: a booked deposit's
 *     `expiresAt` is its dead link window, meaningless once the money has a date;
 *   - on file (no booking) — aged against its 12-month validity (diagram 02), with
 *     past-validity money in its own bucket instead of "≤ 30 days";
 *   - needs a decision (linked only to a dead booking) — shown, not hidden.
 * Held money is ledger money in (`heldMoneyAud`, the server's rule): a part-paid
 * deposit counts what came in, a part-paid pending link counts its part.
 * Invariant: held = booked in + on file + needs a decision; Σ aging = on file.
 */
export function depositPool(
  studioId?: StudioId | null,
  opts: { nowIso?: string; rangeDays?: readonly string[] } = {},
): DepositPool {
  const { deposits, bookings } = useBrainStore.getState();
  const now = new Date(opts.nowIso ?? reportingNow()).getTime();
  const rangeSet = new Set(opts.rangeDays ?? []);
  const byDeposit = bookingsByDepositId(bookings);

  const aging: DepositAgingBucket[] = [
    { key: 'pastValidity', amountAud: 0, count: 0 },
    { key: 'le30', amountAud: 0, count: 0 },
    { key: 'd31to90', amountAud: 0, count: 0 },
    { key: 'd91plus', amountAud: 0, count: 0 },
  ];
  const bucketFor = (daysLeft: number): DepositAgingBucket =>
    daysLeft < 0 ? aging[0]! : daysLeft <= 30 ? aging[1]! : daysLeft <= 90 ? aging[2]! : aging[3]!;

  const pool: DepositPool = {
    heldAud: 0,
    count: 0,
    bookedInAud: 0,
    bookedInCount: 0,
    onFileAud: 0,
    onFileCount: 0,
    floatingAud: 0,
    floatingCount: 0,
    needsDecisionAud: 0,
    needsDecisionCount: 0,
    aging,
    forfeitedInRangeAud: 0,
    forfeitedInRangeCount: 0,
  };

  for (const d of Object.values(deposits)) {
    if (!inStudio(studioId, d.studioId)) continue;

    // Forfeited/expired within the range - money that moved to house.
    if (d.status === 'expired' && rangeSet.has(dayKeyOf(d.expiresAt))) {
      pool.forfeitedInRangeAud += d.amountPaid ?? d.amountAud;
      pool.forfeitedInRangeCount += 1;
    }

    const standing = depositStanding(d, bookings, byDeposit);
    if (standing === 'not-held') continue;
    const heldAud = heldMoneyAud(d);
    pool.heldAud += heldAud;
    pool.count += 1;

    if (standing === 'booked-in') {
      pool.bookedInAud += heldAud;
      pool.bookedInCount += 1;
    } else if (standing === 'needs-decision') {
      pool.needsDecisionAud += heldAud;
      pool.needsDecisionCount += 1;
    } else {
      pool.onFileAud += heldAud;
      pool.onFileCount += 1;
      const bucket = bucketFor((Date.parse(onFileValidUntil(d)) - now) / 86_400_000);
      bucket.amountAud += heldAud;
      bucket.count += 1;
    }
  }
  pool.floatingAud = pool.onFileAud;
  pool.floatingCount = pool.onFileCount;
  return pool;
}

// --- Top-up reporting ------------------------------------------------------

export interface TopupDaysBucket {
  key: 'le7' | 'd8to21' | 'd22plus';
  count: number;
  amountAud: number;
}

export interface TopupReport {
  /** Partial deposits with a balance still owing. */
  count: number;
  outstandingAud: number;
  /** Within the final-reminder window of their session (<= 7 days out). */
  closeToSession: number;
  /** Cadence says a reminder should go now. */
  needNudge: number;
  byState: Record<TopupTrackState, { count: number; amountAud: number }>;
  byDays: TopupDaysBucket[];
}

/**
 * Money-tab snapshot of the top-up book, aggregating the same reminder track the
 * operator screens use (`buildTopupTrack`). A *now* figure, not ranged — top-ups
 * are open balances, like the held deposit pool. `studioId` scopes to one studio.
 */
export function topupReport(studioId?: StudioId | null): TopupReport {
  const { deposits, bookings, customers } = useBrainStore.getState();
  const rows = buildTopupTrack({
    nowIso: reportingNow(),
    deposits,
    bookings,
    customers,
    studioIds: studioId ? [studioId] : undefined,
  });

  const byState: Record<TopupTrackState, { count: number; amountAud: number }> = {
    due: { count: 0, amountAud: 0 },
    'final-due': { count: 0, amountAud: 0 },
    waiting: { count: 0, amountAud: 0 },
    suppressed: { count: 0, amountAud: 0 },
  };
  const byDays: TopupDaysBucket[] = [
    { key: 'le7', count: 0, amountAud: 0 },
    { key: 'd8to21', count: 0, amountAud: 0 },
    { key: 'd22plus', count: 0, amountAud: 0 },
  ];
  let outstandingAud = 0;
  let closeToSession = 0;
  let needNudge = 0;

  for (const r of rows) {
    outstandingAud += r.outstandingAud;
    byState[r.state].count += 1;
    byState[r.state].amountAud += r.outstandingAud;
    if (r.dueForReminder) needNudge += 1;
    const d = r.daysUntilSession;
    // No known session date sits with the far-out bucket (no urgency).
    const bucket = d == null ? byDays[2]! : d <= TOPUP_FINAL_REMINDER_DAYS_BEFORE ? byDays[0]! : d <= 21 ? byDays[1]! : byDays[2]!;
    bucket.count += 1;
    bucket.amountAud += r.outstandingAud;
    if (d != null && d <= TOPUP_FINAL_REMINDER_DAYS_BEFORE) closeToSession += 1;
  }

  return { count: rows.length, outstandingAud, closeToSession, needNudge, byState, byDays };
}

// --- F5: Customers ---------------------------------------------------------

export interface NewReturningDay {
  dayIso: string;
  /** Completed sessions that were the customer's first ever (in scope). */
  newCount: number;
  /** Completed sessions by a customer who had an earlier one. */
  returningCount: number;
}

/**
 * New vs returning customers per day across the window. A completed session
 * counts as "new" when it is that customer's earliest completed session (in
 * scope), else "returning".
 */
export function newVsReturningByDay(days: readonly string[], studioId?: StudioId | null): NewReturningDay[] {
  const { bookings } = useBrainStore.getState();
  const set = new Set(days);
  const timeOf = (b: Booking) => new Date(b.checkedOutAt ?? b.paidAt ?? b.start).getTime();

  const firstMs = new Map<string, number>();
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed' || !inStudio(studioId, b.studioId)) continue;
    const ms = timeOf(b);
    const prev = firstMs.get(b.customerId);
    if (prev === undefined || ms < prev) firstMs.set(b.customerId, ms);
  }

  const perDay = new Map<string, NewReturningDay>(
    days.map((d) => [d, { dayIso: d, newCount: 0, returningCount: 0 }]),
  );
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed' || !inStudio(studioId, b.studioId)) continue;
    const key = completedDayKey(b);
    if (!set.has(key)) continue;
    const cell = perDay.get(key)!;
    if (timeOf(b) === firstMs.get(b.customerId)) cell.newCount += 1;
    else cell.returningCount += 1;
  }
  return days.map((d) => perDay.get(d)!);
}

// --- F5: Close reasons (Prompt A sequence data) ----------------------------

export interface CloseReasonBreakdown {
  /** Leads that converted (booked or completed). */
  won: number;
  /** Reds we never reached (unresponsive). */
  lightReds: number;
  /** Reds who said no (genuine lost). */
  darkReds: number;
  total: number;
  wonPct: number;
  lightRedPct: number;
  darkRedPct: number;
}

/**
 * Close-reason breakdown - closed-won vs light-red (unresponsive) vs dark-red
 * (genuine lost) - "50% close, of which 40% unresponsive and 10% genuine lost".
 * Won is read from `leadStage` (booked/completed); the reds from Prompt A's
 * `sequenceState` red kinds. No new field invented.
 */
export function closeReasonBreakdown(studioId?: StudioId | null): CloseReasonBreakdown {
  const { customers, sequenceState } = useBrainStore.getState();
  let won = 0;
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    if (c.leadStage === 'booked' || c.leadStage === 'completed') won += 1;
  }
  let lightReds = 0;
  let darkReds = 0;
  for (const [customerId, seq] of Object.entries(sequenceState)) {
    if (seq.stage !== 'red') continue;
    if (!inStudio(studioId, studioOf(customerId, customers))) continue;
    if (seq.redKind === 'never-reached') lightReds += 1;
    else if (seq.redKind === 'said-no') darkReds += 1;
  }
  const total = won + lightReds + darkReds;
  const p = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  return {
    won,
    lightReds,
    darkReds,
    total,
    wonPct: p(won),
    lightRedPct: p(lightReds),
    darkRedPct: p(darkReds),
  };
}

// --- G4: Levers + leak reports ---------------------------------------------

/** Booking statuses that are not a live calendar slot (mirrors queueEngine's exclusions). */
const DEAD_BOOKING_STATUSES = new Set<Booking['status']>(['cancelled', 'no-show', 'superseded']);

/** getUTCDay index (0=Sun..6=Sat) -> availability pattern key. */
const DOW_KEYS: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((x) => Number.parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

function addDaysKey(dayKey: string, n: number): string {
  return new Date(new Date(`${dayKey}T12:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Rostered hours for an artist on a day-key, honouring RDO + leave. Mirrors
 * `buildGapList`'s capacity rule (queueEngine) but keyed off the day-string so it
 * stays timezone-deterministic like the rest of this engine. 0 when not working.
 */
function rosteredHoursOn(artist: Artist, dayKey: string): number {
  const dow = DOW_KEYS[dowOf(dayKey)]!;
  const window = artist.availability.pattern[dow];
  if (!window) return 0;
  if (artist.availability.rdoDates.includes(dayKey)) return 0;
  // Leave blocks are instants (a calendar day off is Melbourne midnight as a UTC
  // Z string): key them by the studio day, never slice(0, 10) (B7 / 8uf3 class).
  if (artist.availability.leaveBlocks.some((lb) => dayKey >= studioDayKey(lb.startDate) && dayKey <= studioDayKey(lb.endDate))) {
    return 0;
  }
  return Math.max(0, (hhmmToMinutes(window.endTime) - hhmmToMinutes(window.startTime)) / 60);
}

/** The customer's most recent agent-authored conversation entry's agentId. */
function latestConversationAgentId(c: CustomerRecord | undefined): string | undefined {
  if (!c) return undefined;
  let latestAt = '';
  let agentId: string | undefined;
  for (const e of c.conversation) {
    if (!e.agentId) continue;
    if (e.timestamp >= latestAt) {
      latestAt = e.timestamp;
      agentId = e.agentId;
    }
  }
  return agentId;
}

export interface RescheduleEventRow {
  bookingId: string;
  customerId: string;
  studioId: StudioId;
  reason?: RescheduleReason;
  note?: string;
  /** Local hour (0..23) the reschedule was recorded. */
  createdHour: number;
  /** The booking's source channel (no per-reschedule channel is captured). */
  channel: BookingSource;
  /** Who drove it: the history author when an agent, else the customer's latest agent contact. */
  agentId?: string;
  /** Total conversation touches with the customer. */
  messageCount: number;
  /** True when a new date was picked (move); false = released with no date (the leak). */
  movedToDate: boolean;
  atIso: string;
}

export interface RescheduleEvents {
  count: number;
  byReason: Partial<Record<RescheduleReason, number>>;
  rows: RescheduleEventRow[];
}

/**
 * Every reschedule in the window, read from the G1 keep-original records: one row
 * per `superseded` booking whose last `rescheduleHistory` entry landed in-window.
 * `movedToDate` splits the ones that picked a new date from the leaks (released
 * with no date). Pathway fields (channel, driving agent, hour, touches) drive the
 * Levers-tab drill-down. Newest first.
 *
 * ASSUMPTION: no per-reschedule channel/agent is captured, so `channel` falls back
 * to the booking source and `agentId` to the history author (when an agent) else
 * the customer's most recent agent-authored conversation entry.
 */
export function rescheduleEvents(days: readonly string[], studioId?: StudioId | null): RescheduleEvents {
  const { bookings, customers, agents } = useBrainStore.getState();
  const set = new Set(days);
  const byReason: Partial<Record<RescheduleReason, number>> = {};
  const rows: RescheduleEventRow[] = [];
  for (const b of Object.values(bookings)) {
    if (b.status !== 'superseded') continue;
    if (!inStudio(studioId, b.studioId)) continue;
    const history = b.rescheduleHistory;
    if (!history || history.length === 0) continue;
    const last = history[history.length - 1]!;
    if (!set.has(dayKeyOf(last.at))) continue;
    const customer = customers[b.customerId];
    const agentId = agents[last.by] ? last.by : latestConversationAgentId(customer);
    if (last.reason) byReason[last.reason] = (byReason[last.reason] ?? 0) + 1;
    rows.push({
      bookingId: b.id,
      customerId: b.customerId,
      studioId: b.studioId,
      reason: last.reason,
      note: last.note,
      createdHour: localHourOf(last.at),
      channel: b.source,
      ...(agentId ? { agentId } : {}),
      messageCount: customer?.conversation.length ?? 0,
      movedToDate: !!b.rescheduledToBookingId,
      atIso: last.at,
    });
  }
  rows.sort((a, b) => b.atIso.localeCompare(a.atIso));
  return { count: rows.length, byReason, rows };
}

export interface LeverConversion {
  key: 'deposits' | 'reschedules' | 'rebooks' | 'confirmations';
  label: string;
  converted: number;
  total: number;
  /** total - converted: the ones that slipped this lever. */
  missed: number;
  /** Deposit money (already taken) tied to the missed count on this lever. */
  valueAtRiskAud: number;
  /** converted / total as a percentage; null when total is 0. */
  ratePct: number | null;
}

/**
 * The four lever conversions over the window (Zack's "how many landed"):
 *  - deposits paid -> a live dated booking,
 *  - reschedules -> a new date picked (vs released as a leak),
 *  - in-chair rebooks -> a date (rebookedInChair) out of the completed sessions
 *    that reached a rebook decision,
 *  - confirmations confirmed -> the client actually showed (checked-in / in-chair
 *    / completed, or an actualStart was stamped).
 */
export function leverFunnels(days: readonly string[], studioId?: StudioId | null): LeverConversion[] {
  const { deposits, bookings, confirmationCalls } = useBrainStore.getState();
  const set = new Set(days);
  // Deposit money (already taken) tied to a record; the value at risk when it misses.
  // Real money already taken on the record: ledger money in (the server's rule).
  const depAmt = (d: Deposit | undefined): number => (d ? depositMoneyInAud(d) : 0);
  const bookingDeposit = (b: Booking) =>
    b.depositId ? deposits[b.depositId] : Object.values(deposits).find((d) => d.bookingId === b.id);

  let depTotal = 0;
  let depConverted = 0;
  let depMissedValue = 0;
  for (const d of Object.values(deposits)) {
    if (d.status !== 'paid' || !d.paidAt) continue;
    if (!inStudio(studioId, d.studioId)) continue;
    if (!set.has(dayKeyOf(d.paidAt))) continue;
    depTotal += 1;
    const b = d.bookingId ? bookings[d.bookingId] : undefined;
    if (b && !DEAD_BOOKING_STATUSES.has(b.status)) depConverted += 1;
    else depMissedValue += depAmt(d);
  }

  const resched = rescheduleEvents(days, studioId);
  const resTotal = resched.count;
  const resConverted = resched.rows.filter((r) => r.movedToDate).length;
  const resMissedValue = resched.rows
    .filter((r) => !r.movedToDate)
    .reduce((sum, r) => sum + depAmt(bookings[r.bookingId] ? bookingDeposit(bookings[r.bookingId]!) : undefined), 0);

  let rebookTotal = 0;
  let rebookConverted = 0;
  let rebookMissedValue = 0;
  for (const b of Object.values(bookings)) {
    if (b.status !== 'completed' || !b.rebookOutcome) continue;
    // A final session (piece complete) never reached a rebook decision (tv-2na.4),
    // nor did a one-off with no future booking (B8 / 76vb).
    if (b.rebookOutcome.kind === 'pieceComplete') continue;
    if (isEndOfWorkNoRebook(b, useBrainStore.getState().pieces)) continue;
    if (!inStudio(studioId, b.studioId)) continue;
    if (!set.has(completedDayKey(b))) continue;
    rebookTotal += 1;
    if (rebookedInChair(b)) rebookConverted += 1;
    else rebookMissedValue += depAmt(bookingDeposit(b));
  }

  let confTotal = 0;
  let confConverted = 0;
  let confMissedValue = 0;
  for (const cc of Object.values(confirmationCalls)) {
    if (cc.outcome !== 'confirmed') continue;
    const b = bookings[cc.bookingId];
    if (!b || !inStudio(studioId, b.studioId)) continue;
    if (!set.has(dayKeyOf(cc.scheduledFor))) continue;
    confTotal += 1;
    const showed = b.status === 'checked-in' || b.status === 'in-chair' || b.status === 'completed' || !!b.actualStart;
    if (showed) confConverted += 1;
    else confMissedValue += depAmt(bookingDeposit(b));
  }

  const mk = (
    key: LeverConversion['key'],
    label: string,
    converted: number,
    total: number,
    valueAtRiskAud: number,
  ): LeverConversion => ({
    key,
    label,
    converted,
    total,
    missed: total - converted,
    valueAtRiskAud,
    ratePct: total > 0 ? (converted / total) * 100 : null,
  });

  return [
    mk('deposits', 'Deposits paid to dated booking', depConverted, depTotal, depMissedValue),
    mk('reschedules', 'Reschedules to new date', resConverted, resTotal, resMissedValue),
    mk('rebooks', 'Rebooks to a date', rebookConverted, rebookTotal, rebookMissedValue),
    mk('confirmations', 'Confirmations to showed', confConverted, confTotal, confMissedValue),
  ];
}

export interface DatelessDepositCohort {
  count: number;
  amountAud: number;
  depositIds: string[];
}

/**
 * Deposits "sitting in the wind": money in, no booking at all — the shared
 * on-file rule (@atink/domain `isDepositOnFile`), the same one the held pool
 * ages and money at risk counts. A deposit on a booking that started earlier
 * today, or on today's in-chair / checked-in session, is booked in, not dateless
 * (75nd). Valued at ledger money in. A *now* figure.
 */
export function datelessDepositCohort(studioId?: StudioId | null): DatelessDepositCohort {
  const { deposits, bookings } = useBrainStore.getState();
  const byDeposit = bookingsByDepositId(bookings);
  const out: DatelessDepositCohort = { count: 0, amountAud: 0, depositIds: [] };
  for (const d of Object.values(deposits)) {
    if (!inStudio(studioId, d.studioId)) continue;
    if (!isDepositOnFile(d, bookings, byDeposit)) continue;
    out.count += 1;
    out.amountAud += heldMoneyAud(d);
    out.depositIds.push(d.id);
  }
  out.depositIds.sort();
  return out;
}

// --- Business leaks: every path that ends in no rebook ---------------------

export type LeakCategoryKey =
  | 'reschedule-leaked'
  | 'deposit-no-date'
  | 'no-show'
  | 'declined-in-chair'
  | 'deferred-unresolved'
  | 'will-rebook-later';

export interface LeakPathStep {
  atIso?: string;
  label: string;
  detail?: string;
  /** The terminal step where it leaks (rendered in the alert colour). */
  leak?: boolean;
}

export interface LeakRecord {
  id: string;
  customerId: string;
  name: string;
  subtitle: string;
  /** Real deposit money tied to this record (already taken), not a projection. */
  valueAtRiskAud: number;
  path: LeakPathStep[];
}

export interface LeakCategory {
  key: LeakCategoryKey;
  label: string;
  count: number;
  valueAtRiskAud: number;
  records: LeakRecord[];
}

export interface BusinessLeaks {
  totalCount: number;
  totalValueAtRiskAud: number;
  categories: LeakCategory[];
}

const DECLINE_REASON_LABEL: Record<string, string> = {
  'not-sure-on-design': 'not sure on design',
  'going-overseas': 'going overseas',
  'moving-studios': 'moving studios',
  'needs-to-think': 'needs to think',
  other: 'other',
};
const DEFER_REASON_LABEL: Record<string, string> = {
  'wants-bigger-piece': 'wants a bigger piece',
  'checking-calendar': 'checking calendar',
  'deciding-design': 'deciding design',
  'budget-timing': 'budget / timing',
  other: 'other',
};
const LATER_REASON_LABEL: Record<string, string> = {
  'needs-to-check-diary': 'needs to check diary',
  money: 'money',
  'undecided-design': 'undecided on design',
  other: 'other',
};
const RESCHEDULE_LEAK_REASON_LABEL: Record<RescheduleReason, string> = {
  'gap-fill': 'gap fill',
  'artist-move': 'artist move',
  'client-move': 'client move',
  'cancellation-fill': 'cancellation fill',
  other: 'other',
};

function leakDate(iso: string | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * Every modelled path that ends in no rebook, as drill-down cohorts: reschedules
 * released with no new date, dateless deposits, no-shows with no follow-up date,
 * in-chair declines (deposit applied), sales-deferred that never resolved, and
 * "will rebook later" that never did. Each record carries the deposit money tied
 * to it (already taken — the value at risk) and a reconstructed path from the
 * booking/deposit timestamps. Newest activity first within a cohort.
 */
export function businessLeaks(days: readonly string[], studioId?: StudioId | null): BusinessLeaks {
  const { deposits, bookings, customers, pieces } = useBrainStore.getState();
  const set = new Set(days);
  const now = new Date(reportingNow()).getTime();
  // Real money already taken on the record: ledger money in (the server's rule).
  const depAmt = (d: Deposit | undefined): number => (d ? depositMoneyInAud(d) : 0);
  const nameOf = (customerId: string): string => {
    const c = customers[customerId];
    return c ? customerName(c) : customerId;
  };
  const depositForBooking = (b: Booking) =>
    b.depositId ? deposits[b.depositId] : Object.values(deposits).find((d) => d.bookingId === b.id);
  const hasLiveFutureBooking = (customerId: string): boolean =>
    Object.values(bookings).some(
      (b) => b.customerId === customerId && !DEAD_BOOKING_STATUSES.has(b.status) && new Date(b.start).getTime() >= now,
    );

  // 1. Reschedules released with no new date.
  const reschedule: LeakRecord[] = rescheduleEvents(days, studioId).rows
    .filter((r) => !r.movedToDate)
    .map((r) => {
      const b = bookings[r.bookingId];
      const dep = b ? depositForBooking(b) : undefined;
      const value = depAmt(dep);
      const path: LeakPathStep[] = [];
      if (dep?.paidAt) path.push({ atIso: dep.paidAt, label: `Deposit paid — ${aud(value)}` });
      if (b?.createdAt) path.push({ atIso: b.createdAt, label: `Booking held${b.start ? ` — ${leakDate(b.start)}` : ''}` });
      path.push({
        atIso: r.atIso,
        label: `Rescheduled off${r.reason ? ` — ${RESCHEDULE_LEAK_REASON_LABEL[r.reason]}` : ''}`,
        ...(r.note ? { detail: r.note } : {}),
      });
      if (r.messageCount > 0) path.push({ label: `${r.messageCount} conversation touches on file` });
      path.push({ label: 'No new date — deposit still on file', leak: true });
      return {
        id: r.bookingId,
        customerId: r.customerId,
        name: nameOf(r.customerId),
        subtitle: `was ${leakDate(b?.start)}${r.reason ? ` · ${RESCHEDULE_LEAK_REASON_LABEL[r.reason]}` : ''}`,
        valueAtRiskAud: value,
        path,
      };
    });

  // 2. Dateless deposits (money on file, no booking). A reschedule-released
  // booking's deposit is already on the reschedule record: count it once.
  const rescheduleDepositIds = new Set(
    reschedule.map((r) => (bookings[r.id] ? depositForBooking(bookings[r.id]!)?.id : undefined)).filter(Boolean),
  );
  const datelessIds = new Set(datelessDepositCohort(studioId).depositIds.filter((id) => !rescheduleDepositIds.has(id)));
  const depositNoDate: LeakRecord[] = Object.values(deposits)
    .filter((d) => datelessIds.has(d.id))
    .sort((a, b) => (b.paidAt ?? '').localeCompare(a.paidAt ?? ''))
    .map((d) => {
      const value = depAmt(d);
      return {
        id: d.id,
        customerId: d.customerId,
        name: nameOf(d.customerId),
        subtitle: `paid ${leakDate(d.paidAt)} · no date`,
        valueAtRiskAud: value,
        path: [
          ...(d.paidAt ? [{ atIso: d.paidAt, label: `Deposit paid — ${aud(value)}` }] : []),
          { label: 'No booking made — money on file with nowhere to land', leak: true },
        ],
      };
    });

  // Completed-session outcomes (declined / deferred / will-rebook) + no-shows.
  const noShow: LeakRecord[] = [];
  const declined: LeakRecord[] = [];
  const deferred: LeakRecord[] = [];
  const willRebook: LeakRecord[] = [];

  for (const b of Object.values(bookings)) {
    if (!inStudio(studioId, b.studioId)) continue;
    const dep = depositForBooking(b);
    const value = depAmt(dep);

    if (b.status === 'no-show' && set.has(dayKeyOf(b.start)) && !hasLiveFutureBooking(b.customerId)) {
      noShow.push({
        id: b.id,
        customerId: b.customerId,
        name: nameOf(b.customerId),
        subtitle: `missed ${leakDate(b.start)}`,
        valueAtRiskAud: value,
        path: [
          ...(b.createdAt ? [{ atIso: b.createdAt, label: 'Booking confirmed' }] : []),
          { atIso: b.start, label: `No-show — missed ${leakDate(b.start)}`, leak: false },
          { label: 'No follow-up date booked', leak: true },
        ],
      });
      continue;
    }

    if (b.status !== 'completed' || !b.rebookOutcome) continue;
    if (!set.has(completedDayKey(b))) continue;
    const o = b.rebookOutcome;
    const when = b.checkedOutAt ?? b.start;

    if (o.kind === 'noRebookDepositApplied') {
      if (isEndOfWorkNoRebook(b, pieces)) continue;
      declined.push({
        id: b.id,
        customerId: b.customerId,
        name: nameOf(b.customerId),
        subtitle: `declined ${leakDate(when)} · ${DECLINE_REASON_LABEL[o.reason] ?? o.reason}`,
        valueAtRiskAud: value,
        path: [
          { atIso: when, label: 'Session completed' },
          {
            atIso: when,
            label: `Declined a rebook in chair — ${DECLINE_REASON_LABEL[o.reason] ?? o.reason}`,
            ...(o.reasonNote ? { detail: o.reasonNote } : {}),
          },
          { label: 'Deposit applied — left with no next date', leak: true },
        ],
      });
    } else if (o.kind === 'rebookDeferredToSales' && o.resolvedOutcome !== 'booked' && o.resolvedOutcome !== 'declined') {
      deferred.push({
        id: b.id,
        customerId: b.customerId,
        name: nameOf(b.customerId),
        subtitle: `deferred ${leakDate(when)}${o.reason ? ` · ${DEFER_REASON_LABEL[o.reason] ?? o.reason}` : ''}`,
        valueAtRiskAud: value,
        path: [
          { atIso: when, label: 'Session completed' },
          { atIso: when, label: `Handed to sales${o.reason ? ` — ${DEFER_REASON_LABEL[o.reason] ?? o.reason}` : ''}` },
          { label: 'Sales made no contact — still no date', leak: true },
        ],
      });
    } else if (o.kind === 'willRebookLater' && !hasLiveFutureBooking(b.customerId)) {
      willRebook.push({
        id: b.id,
        customerId: b.customerId,
        name: nameOf(b.customerId),
        subtitle: `soft yes ${leakDate(when)}${o.reason ? ` · ${LATER_REASON_LABEL[o.reason] ?? o.reason}` : ''}`,
        valueAtRiskAud: value,
        path: [
          { atIso: when, label: 'Session completed' },
          {
            atIso: when,
            label: `Said they'd rebook later${o.reason ? ` — ${LATER_REASON_LABEL[o.reason] ?? o.reason}` : ''}`,
            ...(o.note ? { detail: o.note } : {}),
          },
          { label: 'No booking made since', leak: true },
        ],
      });
    }
  }

  const mkCat = (key: LeakCategoryKey, label: string, records: LeakRecord[]): LeakCategory => ({
    key,
    label,
    count: records.length,
    valueAtRiskAud: records.reduce((sum, r) => sum + r.valueAtRiskAud, 0),
    records,
  });

  const categories: LeakCategory[] = [
    mkCat('reschedule-leaked', 'Reschedule, never re-dated', reschedule),
    mkCat('deposit-no-date', 'Deposit, no date', depositNoDate),
    mkCat('no-show', 'No-show, not rebooked', noShow),
    mkCat('declined-in-chair', 'Declined in chair', declined),
    mkCat('deferred-unresolved', 'Deferred to sales', deferred),
    mkCat('will-rebook-later', '"Will rebook later"', willRebook),
  ];

  return {
    totalCount: categories.reduce((sum, c) => sum + c.count, 0),
    totalValueAtRiskAud: categories.reduce((sum, c) => sum + c.valueAtRiskAud, 0),
    categories,
  };
}

/** ASSUMPTION: X = 80; client sets the real forward-book threshold. */
export const WEEKS_BOOKED_THRESHOLD = 0.8;
/** Cap the look-ahead at a quarter of weeks. */
export const WEEKS_BOOKED_LOOKAHEAD = 12;

export interface WeekUtilisation {
  /** 'YYYY-MM-DD' of the week's first day (today for week 0). */
  startDayKey: string;
  capacityHours: number;
  usedHours: number;
  /** used / capacity as a fraction 0..1; 0 when no capacity. */
  utilisedPct: number;
  atOrAboveThreshold: boolean;
}

export interface WeeksBookedAhead {
  /** Consecutive whole weeks from today at/above threshold before the first with slack. */
  weeks: number;
  thresholdPct: number;
  lookaheadWeeks: number;
  weekly: WeekUtilisation[];
}

/**
 * Forward-book depth: how many whole weeks from today are >= threshold utilised
 * before the first week with free capacity. Utilisation is REAL booked hours over
 * rostered capacity (pattern hours minus RDO/leave), reusing `buildGapList`'s
 * capacity rule. Answers Zack's "how many weeks ahead are we booked".
 */
export function weeksBookedAhead(studioId?: StudioId | null): WeeksBookedAhead {
  const { artists, bookings } = useBrainStore.getState();
  const todayKey = dayKeyOf(reportingNow());
  const artistList = Object.values(artists)
    .filter((a) => !a.archived && !a.isApprentice)
    .filter((a) => !studioId || a.studioId === studioId);

  // Live booked hours per artist per day-key, one pass (excludes dead bookings).
  const usedByArtistDay = new Map<string, Map<string, number>>();
  for (const b of Object.values(bookings)) {
    if (DEAD_BOOKING_STATUSES.has(b.status)) continue;
    if (studioId && b.studioId !== studioId) continue;
    const perDay = usedByArtistDay.get(b.artistId) ?? new Map<string, number>();
    const key = dayKeyOf(b.start);
    perDay.set(key, (perDay.get(key) ?? 0) + b.durationMin / 60);
    usedByArtistDay.set(b.artistId, perDay);
  }

  const weekly: WeekUtilisation[] = [];
  for (let w = 0; w < WEEKS_BOOKED_LOOKAHEAD; w += 1) {
    let capacityHours = 0;
    let usedHours = 0;
    for (let d = 0; d < 7; d += 1) {
      const dayKey = addDaysKey(todayKey, w * 7 + d);
      for (const artist of artistList) {
        const cap = rosteredHoursOn(artist, dayKey);
        if (cap <= 0) continue;
        capacityHours += cap;
        usedHours += usedByArtistDay.get(artist.id)?.get(dayKey) ?? 0;
      }
    }
    const utilisedPct = capacityHours > 0 ? usedHours / capacityHours : 0;
    weekly.push({
      startDayKey: addDaysKey(todayKey, w * 7),
      capacityHours: round1(capacityHours),
      usedHours: round1(usedHours),
      utilisedPct,
      atOrAboveThreshold: capacityHours > 0 && utilisedPct >= WEEKS_BOOKED_THRESHOLD,
    });
  }

  let weeks = 0;
  for (const wk of weekly) {
    if (!wk.atOrAboveThreshold) break;
    weeks += 1;
  }
  return { weeks, thresholdPct: WEEKS_BOOKED_THRESHOLD, lookaheadWeeks: WEEKS_BOOKED_LOOKAHEAD, weekly };
}

// --- G5: Ops-sheet parity selectors ----------------------------------------

/**
 * Booked sessions backed by a deposit (14 Jul: "deposit = booked session"). Four
 * sessions on one piece deposit count as four. A session is backed by a direct
 * `booking.depositId` or a piece-level deposit (`deposit.pieceId === booking.pieceId`).
 * Counts live sessions whose start day is in the window - a session count, kept
 * SEPARATE from the dollar selectors (`depositsPaidByDay`).
 */
export function depositSessionsCount(days: readonly string[], studioId?: StudioId | null): number {
  const { bookings, deposits } = useBrainStore.getState();
  const set = new Set(days);
  const depositIds = new Set<string>();
  const depositPieceIds = new Set<string>();
  for (const d of Object.values(deposits)) {
    depositIds.add(d.id);
    if (d.pieceId) depositPieceIds.add(d.pieceId);
  }
  let count = 0;
  for (const b of Object.values(bookings)) {
    if (DEAD_BOOKING_STATUSES.has(b.status)) continue;
    if (!inStudio(studioId, b.studioId)) continue;
    if (!set.has(dayKeyOf(b.start))) continue;
    const backed =
      (b.depositId ? depositIds.has(b.depositId) : false) ||
      (b.pieceId ? depositPieceIds.has(b.pieceId) : false);
    if (backed) count += 1;
  }
  return count;
}

export interface ArtistLinkDepositRow {
  artistId: string;
  count: number;
  amountAud: number;
  clientNames: string[];
}

export interface ArtistLinkDeposits {
  total: number;
  amountAud: number;
  byArtist: ArtistLinkDepositRow[];
}

/**
 * Artist-link deposits in the window - the "booked totals" from Zack's sheet.
 * Filters on `deposit.source === 'artist-link'` (NOT a reference prefix). Paid
 * deposits only; grouped per artist with the client names. Reported separately
 * from - and excluded from - the social-conversion strike rate below.
 */
export function artistLinkDeposits(days: readonly string[], studioId?: StudioId | null): ArtistLinkDeposits {
  const { deposits, customers } = useBrainStore.getState();
  const set = new Set(days);
  const rows = new Map<string, ArtistLinkDepositRow>();
  let total = 0;
  let amountAud = 0;
  for (const d of Object.values(deposits)) {
    if (d.source !== 'artist-link') continue;
    if (d.status !== 'paid' || !d.paidAt) continue;
    if (!inStudio(studioId, d.studioId)) continue;
    if (!set.has(dayKeyOf(d.paidAt))) continue;
    const artistId = d.artistId ?? 'unassigned';
    const amt = d.amountPaid ?? d.amountAud;
    total += 1;
    amountAud += amt;
    const row = rows.get(artistId) ?? { artistId, count: 0, amountAud: 0, clientNames: [] };
    row.count += 1;
    row.amountAud += amt;
    const cust = customers[d.customerId];
    if (cust) row.clientNames.push(customerName(cust));
    rows.set(artistId, row);
  }
  return {
    total,
    amountAud,
    byArtist: [...rows.values()].sort((a, b) => b.count - a.count || a.artistId.localeCompare(b.artistId)),
  };
}

export interface SocialConversion {
  socialLeads: number;
  conversions: number;
  excludedArtistLink: number;
  strikeRatePct: number | null;
}

/**
 * Social-DM conversion strike rate with artist-link deposits EXCLUDED (Zack's
 * "99 - 12 = 87 of 134"): of social-source leads with a first touch in the window,
 * the share that paid a NON-artist-link deposit. Artist-link conversions are
 * tallied separately so they never inflate the social number.
 *
 * ASSUMPTION: "social lead" = `leadSourceGroup` 'social'; conversion is per lead.
 */
export function socialConversionStrikeRate(days: readonly string[], studioId?: StudioId | null): SocialConversion {
  const { customers, deposits } = useBrainStore.getState();
  const set = new Set(days);
  const socialIds = new Set<string>();
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    if (leadSourceGroup(c) !== 'social') continue;
    if (!set.has(dayKeyOf(c.source.firstTouchAt))) continue;
    socialIds.add(c.id);
  }
  const converted = new Set<string>();
  const excluded = new Set<string>();
  for (const d of Object.values(deposits)) {
    if (d.status !== 'paid') continue;
    if (!socialIds.has(d.customerId)) continue;
    if (d.source === 'artist-link') excluded.add(d.customerId);
    else converted.add(d.customerId);
  }
  const socialLeads = socialIds.size;
  const conversions = converted.size;
  return {
    socialLeads,
    conversions,
    excludedArtistLink: excluded.size,
    strikeRatePct: socialLeads > 0 ? (conversions / socialLeads) * 100 : null,
  };
}

export interface ReviewsDelta {
  thisWeek: number;
  lastWeek: number;
  delta: number;
  target: number;
  meetsTarget: boolean;
}

/**
 * Reviews this window vs the equal window before it vs target, summed across the
 * studios in scope. Reads the seeded `reviewsFixture` - there is no in-app reviews
 * workflow yet, so this stands in for one. `// ASSUMPTION: seeded until a reviews
 * source exists.`
 */
export function reviewsDelta(days: readonly string[], studioId?: StudioId | null): ReviewsDelta {
  const { studios } = useBrainStore.getState();
  const scope = Object.values(studios).filter((s) => !s.archived && (!studioId || s.id === studioId));
  const prev = previousWindowDays(days);
  let thisWeek = 0;
  let lastWeek = 0;
  let target = 0;
  for (const s of scope) {
    thisWeek += reviewsInRange(s.id, days);
    lastWeek += reviewsInRange(s.id, prev);
    target += studioReviewsTarget(s.id);
  }
  return { thisWeek, lastWeek, delta: thisWeek - lastWeek, target, meetsTarget: thisWeek >= target };
}

export interface ConversionStatus {
  key: string;
  label: string;
  count: number;
}

/**
 * The lead status block from Zack's sheet, derived from `leadStage` plus the
 * sequence red split (no new fields invented; same sources as
 * `closeReasonBreakdown`). Plain-language buckets approximate the sheet's colour
 * columns - the tooltip maps them for the reader.
 */
export function conversionStatusBlock(studioId?: StudioId | null): ConversionStatus[] {
  const { customers, sequenceState } = useBrainStore.getState();
  const counts = { new: 0, active: 0, booked: 0, sorted: 0, lapsed: 0, doNotContact: 0, unresponsive: 0, closed: 0 };
  for (const c of Object.values(customers)) {
    if (!inStudio(studioId, c.primaryStudioId)) continue;
    const seq = sequenceState[c.id];
    if (seq?.stage === 'red') {
      if (seq.redKind === 'never-reached') counts.unresponsive += 1;
      else counts.closed += 1;
      continue;
    }
    switch (c.leadStage) {
      case 'new':
        counts.new += 1;
        break;
      case 'qualifying':
      case 'deposit-pending':
        counts.active += 1;
        break;
      case 'booked':
        counts.booked += 1;
        break;
      case 'completed':
        counts.sorted += 1;
        break;
      case 'lapsed':
      case 'dormant':
        counts.lapsed += 1;
        break;
      case 'do-not-contact':
        counts.doNotContact += 1;
        break;
    }
  }
  return [
    { key: 'new', label: 'New', count: counts.new },
    { key: 'active', label: 'Active (green)', count: counts.active },
    { key: 'booked', label: 'Booked', count: counts.booked },
    { key: 'sorted', label: 'Sorted (completed)', count: counts.sorted },
    { key: 'unresponsive', label: 'Unresponsive (light red)', count: counts.unresponsive },
    { key: 'closed', label: 'Closed (dark red)', count: counts.closed },
    { key: 'lapsed', label: 'Lapsed', count: counts.lapsed },
    { key: 'do-not-contact', label: 'Do not contact', count: counts.doNotContact },
  ];
}

/**
 * New clients booked per artist in the window: a customer whose first-ever live
 * booking (earliest session) lands in the window, credited to that artist's chair.
 * Feeds the Targets board's lead-threshold flag column via `leadThresholdBand`.
 */
export function newClientsByArtistWeek(days: readonly string[], studioId?: StudioId | null): Record<string, number> {
  const { bookings } = useBrainStore.getState();
  const set = new Set(days);
  const earliest = new Map<string, { start: string; artistId: string; studioId: StudioId }>();
  for (const b of Object.values(bookings)) {
    if (DEAD_BOOKING_STATUSES.has(b.status)) continue;
    const cur = earliest.get(b.customerId);
    if (!cur || b.start < cur.start) {
      earliest.set(b.customerId, { start: b.start, artistId: b.artistId, studioId: b.studioId });
    }
  }
  const out: Record<string, number> = {};
  for (const e of earliest.values()) {
    if (!inStudio(studioId, e.studioId)) continue;
    if (!set.has(dayKeyOf(e.start))) continue;
    out[e.artistId] = (out[e.artistId] ?? 0) + 1;
  }
  return out;
}

export interface ArtistRebookClient {
  bookingId: string;
  customerId: string;
  name: string;
}

export interface ArtistResultsFeed {
  appointmentsThisWeek: number;
  rebooksThisWeek: number;
  /** This week's in-chair rebooks, expandable to the client names on the dashboard. */
  rebookClients: ArtistRebookClient[];
  /** Consecutive most-recent completed sessions that were rebooked in the chair. */
  rebookStreak: number;
}

/**
 * One artist's own live results for their dashboard (14 Jul: artists see their own
 * numbers, never anyone else's, never targets). Appointments + in-chair rebooks
 * this week, the client names behind the rebooks, and the current rebook streak.
 * Replaces the hardcoded earnings tiles.
 */
export function artistResultsFeed(artistId: string, now: string = reportingNow()): ArtistResultsFeed {
  const { bookings, customers } = useBrainStore.getState();
  // artists-6 / 7w8g: the artist's "this week" is the CALENDAR week (Mon–Sun
  // studio days), the same window as their header tile and Week tab. Only
  // committed sessions count (a hold still waiting on a deposit isn't one).
  const week = new Set(calendarWeekDays(now));
  let appointmentsThisWeek = 0;
  const rebookClients: ArtistRebookClient[] = [];
  const completed: Array<{ at: number; rebooked: boolean }> = [];
  for (const b of Object.values(bookings)) {
    if (b.artistId !== artistId) continue;
    if (isCommittedBooking(b) && week.has(dayKeyOf(b.start))) appointmentsThisWeek += 1;
    if (b.status === 'completed') {
      const rebooked = rebookedInChair(b);
      completed.push({ at: new Date(b.checkedOutAt ?? b.paidAt ?? b.start).getTime(), rebooked });
      if (rebooked && week.has(completedDayKey(b))) {
        const cust = customers[b.customerId];
        rebookClients.push({ bookingId: b.id, customerId: b.customerId, name: cust ? customerName(cust) : b.customerId });
      }
    }
  }
  completed.sort((a, b) => b.at - a.at);
  let rebookStreak = 0;
  for (const c of completed) {
    if (!c.rebooked) break;
    rebookStreak += 1;
  }
  return { appointmentsThisWeek, rebooksThisWeek: rebookClients.length, rebookClients, rebookStreak };
}
